import React, { useState, useEffect, useMemo } from 'react';
import { formatFirstName, formatSurname } from '../utils/personFields';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import {
    ArrowLeft, Building2, Loader2, Lock, HardDrive, Wrench, KeyRound,
    AlertTriangle, SlidersHorizontal, LayoutGrid, ShieldCheck,
    Info, Globe2, ShieldOff, CalendarClock, Boxes, Sparkles,
    Tag, LifeBuoy, Users, Link2, BadgeIndianRupee, Pill,
} from 'lucide-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
    createTenant, updateTenant, getTenantById,
    getTenantStorage, saveTenantStorage, testTenantStorage,
    getFeatureCatalogue, getTenantFeatures, saveTenantFeatures,
    getMaintenanceAdmin, saveMaintenanceAdmin, setMaintenanceAdminStatus,
    setOpenPlatform, getFeaturePrices, setTenantPatientCodePrefix,
} from '../redux/tenantSlice';
import { docPatCreationCountryList } from '../redux/masterSlice';
import { SUPPORT_SLA_OPTIONS } from '../redux/featureSlice';
import { restrictnavigate, tryingnavigate, clearPendingNavigation } from '../redux/blockingSlice';
import SearchableSelect from '../components/common/SearchableSelect';
import { isSuperAdminSession } from '../utils/platformPermissions';
import { unlessSystemId } from '../utils/systemId';
import TenantPaymentTab from './tenantBilling/TenantPaymentTab';
import { saveTenantBillingSettings, createTenantPlan } from '../services/tenantBillingApi';

// Create / edit a tenant (super_admin) as a FULL PAGE — replaces the old
// AddTenant modal (decision: no modal; "Add Tenant" navigates here, back
// button returns to the list).
//
// The page renders the services-first structure from the India-SaaS
// restructure: the six services with their own locked cores, then Common
// features. Everything it renders comes from the server catalogue (services,
// tiers, types, cores, dependencies) — no frontend copy to drift.
//
// TENANT TYPE and SERVICE TIER are UNRELATED and live on different tabs, which
// is the whole point of the split:
//   · Tenant type — a descriptive classification ("Small Clinic" vs
//     "Multispecialty Hospital"). Details tab. Carries no services and gates
//     nothing; it is a label a patient reads.
//   · Service tier — a fixed cumulative ladder (tier 0 medi_consult … tier 5
//     + ambulance) that PRE-FILLS the service toggles. Services tab. Never
//     locks anything: every toggle stays editable after a tier is applied.
// They were briefly one concept, where a type decided which services existed
// and which were visible. That conflation is gone — all six services are
// always visible here, and no service is ever "optional for this type".
//
// COUNTRY, SUBDOMAIN and CONNECTION MODE are fixed at creation (the API
// enforces it; subdomain may be assigned once later if it was left empty).

// Same limit as the API (tenantManagementController.validateCity).
const CITY_MAX = 100;

const EMPTY = {
    name: '', countryId: '', contactEmail: '', contactPhone: '',
    // The hospital's city (owner, 29 Sep 2026) — optional, editable any time.
    // The sign-in hospital picker prints it so two hospitals of the same name
    // can be told apart without a system id.
    city: '',
    subdomain: '', connectionMode: 'connected', tenantType: '',
    // WHERE THIS TENANT'S PEOPLE SIGN IN (owner, 25 Sep 2026). 'shared' = temp
    // mode on app.<domain>, typing the subdomain — every tenant starts here,
    // since infra creates its DNS record only once the customer commits.
    // 'subdomain' = its own address only (appUrl); the shared host then
    // refuses its logins. Editable any time — flipped once the record is live.
    hostMode: 'shared', appUrl: '',
    // '' = platform default (sent as null); otherwise one of SUPPORT_SLA_OPTIONS.
    // Editable at any time, unlike the three fixed-at-creation fields above.
    supportLevel: '',
    // The hospital's patient-code prefix (APO -> APO001…). Saved through its
    // OWN endpoint, not the tenant PUT: the write backfills / renames codes
    // server-side. Empty = leave whatever the tenant has untouched.
    patientIdPrefix: '',
};
const prefixRe = /^[A-Za-z]{2,5}$/;
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const subdomainRe = /^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])?$/;

// Where tenant subdomains live — <subdomain>.<this>. Mirrors the nginx setup.
const TENANT_DOMAIN = import.meta.env.VITE_TENANT_DOMAIN || 'mitra-one-care.com';

const EMPTY_STORAGE = {
    bucket: '', region: 'ap-south-1', endpoint: '', keyPrefix: '',
    accessKeyId: '', secretAccessKey: '', isActive: true,
};

const field = 'w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';

// Plan pricing is INR-only and whole-rupee by product decision — no paise in a
// figure a salesperson reads out loud, and no currency picker to keep in sync
// with the server, which stores `currency: 'INR'` flat.
const inrFormatter = new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
});
const inr = (n) => inrFormatter.format(Number.isFinite(n) ? n : 0);

/* ---------- small presentational pieces (mockup design) ---------- */

const Badge = ({ tone, children }) => {
    const tones = {
        core: 'bg-[#E7F5F4] text-[#2D8A8A] border-[#4ABDBD]',
        gold: 'bg-[#FBF1E4] text-[#7A5320] border-[#D7A05C]',
        hold: 'bg-gray-100 text-gray-500 border-gray-300',
    };
    return (
        <span className={`text-[9px] font-extrabold tracking-wider uppercase border rounded-full px-2 py-0.5 ${tones[tone] || tones.core}`}>
            {children}
        </span>
    );
};

const Switch = ({ checked, disabled, onChange, testid }) => (
    <button
        type="button" role="switch" aria-checked={checked} disabled={disabled}
        data-testid={testid}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors ${
            disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
        } ${checked ? 'bg-[#4ABDBD]' : 'bg-gray-300'}`}
    >
        <span className={`inline-block h-[18px] w-[18px] rounded-full bg-white shadow transform transition-transform ${
            checked ? 'translate-x-[22px]' : 'translate-x-[3px]'
        }`} />
    </button>
);

// The two login-group settings that shape the SECOND identifier. They are the
// only ones ignored (and greyed) under 'both' / 'either'.
const IDENTIFIER_SUB_KEYS = ['auth.secondaryIdentifierRequired', 'auth.secondaryIdentifierOtp'];

// WHERE THE DOCTORS' MEDICINES COME FROM (owner, 12 Oct 2026) — a Super-Admin
// tenant setting (backend services/doctorMedicineSource.js), drawn inside the
// Medi-Consult card. 'pharmacy' and 'both' need the Pharma service; switching
// Pharma off here puts the choice back on Manual, as the server does on save.
const MEDICINE_SOURCE_KEY = 'doctor.medicineSource';
const MEDICINE_SOURCES = ['manual', 'pharmacy', 'both'];

const SectionTitle = ({ step, children, first }) => (
    <div className={`flex items-center gap-3 ${first ? 'mt-0' : 'mt-8'} mb-3`}>
        {step != null && (
            <span className="flex-none w-5 h-5 rounded-md bg-[#4ABDBD] text-white text-[10px] font-extrabold grid place-items-center">
                {step}
            </span>
        )}
        <h3 className="text-[11px] font-extrabold uppercase tracking-widest text-[#2D8A8A]">{children}</h3>
        <div className="flex-1 h-px bg-[#C7D6D5]" />
    </div>
);

// The mockup's four note tones, one component so every callout on the page has
// the same padding, icon size and line height.
const Note = ({ tone = 'teal', icon, children }) => {
    const tones = {
        teal: 'bg-[#E7F5F4] border-[#4ABDBD] text-[#2D8A8A]',
        gold: 'bg-[#FBF1E4] border-[#D7A05C] text-[#7A5320]',
        red: 'bg-red-50 border-red-300 text-red-800',
        gray: 'bg-gray-50 border-gray-200 text-gray-600',
    };
    const NoteIcon = icon || Info;
    return (
        <div className={`rounded-xl border px-3.5 py-2.5 text-[12px] leading-relaxed flex items-start gap-2.5 ${tones[tone] || tones.teal}`}>
            <NoteIcon className="w-4 h-4 mt-0.5 flex-none" />
            <span className="min-w-0">{children}</span>
        </div>
    );
};

const FieldLabel = ({ required, children }) => (
    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
        {children}{required && <span className="text-red-500"> *</span>}
    </label>
);

// A value the API fixes at creation. Rendered as a field-shaped box rather than
// hidden, so the page still answers "what was chosen here?" after the fact.
const LockedValue = ({ children, mono }) => (
    <div className={`flex items-center gap-2 px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-600 ${mono ? 'font-mono' : ''}`}>
        <Lock className="w-3.5 h-3.5 text-gray-400 flex-none" />
        <span className="min-w-0 truncate">{children}</span>
    </div>
);

// One radio option drawn as a card (connection mode, login identifier). A
// locked card keeps the SELECTED one at full strength and dims the rest, so a
// permanent choice still reads as a choice that was made.
const ChoiceCard = ({ selected, disabled, onSelect, title, desc, icon: Icon, testid }) => (
    <button
        type="button" data-testid={testid} disabled={disabled}
        role="radio" aria-checked={selected}
        onClick={onSelect}
        className={`text-left rounded-xl border px-3.5 py-3 w-full transition-colors ${
            selected ? 'border-[#4ABDBD] bg-[#E7F5F4]' : 'border-gray-200 bg-white'
        } ${disabled
            ? (selected ? 'cursor-default' : 'opacity-45 cursor-not-allowed')
            : 'cursor-pointer hover:border-[#4ABDBD]'}`}
    >
        <span className="flex items-center gap-2">
            <span className={`flex-none w-4 h-4 rounded-full border-2 grid place-items-center ${
                selected ? 'border-[#4ABDBD]' : 'border-gray-300'
            }`}>
                {selected && <span className="w-2 h-2 rounded-full bg-[#4ABDBD]" />}
            </span>
            {Icon && <Icon className={`w-4 h-4 flex-none ${selected ? 'text-[#2D8A8A]' : 'text-gray-400'}`} />}
            <span className="text-sm font-bold text-gray-800">{title}</span>
            {disabled && selected && <Lock className="w-3.5 h-3.5 text-gray-400 ml-auto flex-none" />}
        </span>
        {desc && <span className="block text-[11px] text-gray-500 mt-1.5 leading-snug">{desc}</span>}
    </button>
);

export default function TenantForm() {
    const { t } = useTranslation(['tenantpage']);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { tenantId: routeTenantId } = useParams();

    const isEdit = Boolean(routeTenantId);
    const [createdTenantId, setCreatedTenantId] = useState(null);
    const workingTenantId = routeTenantId || createdTenantId;
    // Once the tenant exists (edit, or creation succeeded) the page behaves
    // per-tab: each tab has its own Save, exactly like the old modal did.
    const exists = Boolean(workingTenantId);

    const mutating = useSelector((s) => s.tenant?.mutating);
    const countryTimezoneList = useSelector((s) => s.master?.countryTimezoneList);
    // The row being edited: passed by the list page, fetched as a fallback so a
    // page refresh on /tenants/:id/edit still works.
    const stateTenantRaw = useSelector((s) => s.tenant?.tenant);
    const fetchedTenant = stateTenantRaw?.tenant || stateTenantRaw;
    const seedTenant = location.state?.tenant
        || (isEdit && fetchedTenant?.tenantId === routeTenantId ? fetchedTenant : null);

    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [tab, setTab] = useState('details');
    // What the server currently holds for patientIdPrefix — the PATCH is sent
    // only when the field differs from this, and empty means "leave it".
    const [savedPrefix, setSavedPrefix] = useState('');

    // UNSAVED-CHANGES GUARD ---------------------------------------------
    // Dirtiness is tracked from the user's own edits, never by diffing state
    // against a baseline: the seeding effects below fill the form
    // asynchronously as each request lands, and a diff would read those as
    // edits and block navigation on a form nobody touched.
    const [dirty, setDirty] = useState(false);
    const markDirty = () => setDirty(true);
    const { trynavigate } = useSelector((st) => st.blocking);

    /* ---------- catalogue (services / features / types / tiers) ---------- */
    const featureGroups = useSelector((s) => s.tenant?.featureGroups);
    const servicesCatalogue = useSelector((s) => s.tenant?.servicesCatalogue);
    const globalCoreIds = useSelector((s) => s.tenant?.globalCoreIds);
    const serviceCoreIds = useSelector((s) => s.tenant?.serviceCoreIds);
    const tenantTypes = useSelector((s) => s.tenant?.tenantTypes);
    const featureDependencies = useSelector((s) => s.tenant?.featureDependencies);
    const savedFeatures = useSelector((s) => s.tenant?.features);
    const tenantServices = useSelector((s) => s.tenant?.tenantServices);
    const tenantMeta = useSelector((s) => s.tenant?.tenantMeta);
    const tenantSettings = useSelector((s) => s.tenant?.tenantSettings);
    const settingsCatalogue = useSelector((s) => s.tenant?.settingsCatalogue);
    const featuresLoading = useSelector((s) => s.tenant?.featuresLoading);
    const featuresSaving = useSelector((s) => s.tenant?.featuresSaving);
    const featurePrices = useSelector((s) => s.tenant?.featurePrices);
    const featurePricesLoading = useSelector((s) => s.tenant?.featurePricesLoading);

    const [featureForm, setFeatureForm] = useState({});
    // Which of the six services this tenant buys. All six are always shown —
    // nothing hides a service card any more.
    const [services, setServices] = useState({});

    const serviceMap = useMemo(
        () => Object.fromEntries((servicesCatalogue || []).map((s) => [s.id, s])),
        [servicesCatalogue]
    );

    const isGlobalCore = (id) => (globalCoreIds || []).includes(id);
    const isServiceCore = (svcId, id) => ((serviceCoreIds || {})[svcId] || []).includes(id);
    const serviceOn = (svcId) => !svcId || svcId === 'common' || Boolean(services[svcId]);

    // The map as it will be SAVED: every catalogue service named, true only
    // where ticked, a service on hold never on (30 Sep 2026). The server
    // reads a service a posted map leaves out as OFF, so this is what makes
    // the request say exactly what the cards show.
    const completeServices = (map) => {
        const out = {};
        (servicesCatalogue || []).forEach((s) => {
            out[s.id] = s.status !== 'hold' && map?.[s.id] === true;
        });
        return out;
    };

    // Dependency walk — same rules as Backend/config/tenantFeatures.js
    // applyDependencies(); the map itself comes from the server.
    const parentsOf = (id, seen = new Set()) => {
        for (const parent of (featureDependencies?.[id] || [])) {
            if (seen.has(parent)) continue;
            seen.add(parent);
            parentsOf(parent, seen);
        }
        return seen;
    };
    const childrenOf = (id, seen = new Set()) => {
        for (const [child, parents] of Object.entries(featureDependencies || {})) {
            if (!parents.includes(id) || seen.has(child)) continue;
            seen.add(child);
            childrenOf(child, seen);
        }
        return seen;
    };

    const lockedIds = useMemo(() => {
        const out = new Set(globalCoreIds || []);
        Object.entries(serviceCoreIds || {}).forEach(([svc, ids]) => {
            if (serviceOn(svc)) ids.forEach((id) => out.add(id));
        });
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalCoreIds, serviceCoreIds, services]);

    // Features ON HOLD (catalogue `status: 'hold'`, e.g. Diagnosis) — the
    // feature-level twin of a service on hold. Shown as "Soon", never tickable,
    // never charged: the server forces them off whatever is posted.
    const holdFeatureIds = useMemo(() => new Set(
        (featureGroups || []).flatMap((g) => (g.features || []).filter((f) => f.status === 'hold').map((f) => f.id))
    ), [featureGroups]);

    const toggleFeature = (id, on) => {
        if (lockedIds.has(id) && !on) return;
        if (holdFeatureIds.has(id) && on) return;
        markDirty();
        setFeatureForm((prev) => {
            const next = { ...prev, [id]: on };
            if (on) parentsOf(id).forEach((p) => { next[p] = true; });
            else childrenOf(id).forEach((c) => { if (!lockedIds.has(c)) next[c] = false; });
            return next;
        });
    };

    // Services are picked one by one, always. There is no tier/preset shortcut
    // and no "mode" to be in — the six toggles below ARE the choice.
    const toggleService = (svcId, on) => {
        if (serviceMap[svcId]?.status === 'hold') return;
        setServices({ ...services, [svcId]: on });
        if (svcId === 'pharma' && !on) {
            setSettingsForm((prev) => (prev[MEDICINE_SOURCE_KEY] && prev[MEDICINE_SOURCE_KEY] !== 'manual'
                ? { ...prev, [MEDICINE_SOURCE_KEY]: 'manual' }
                : prev));
        }
        markDirty();
    };

    /* ---------- plan pricing (SUPER ADMIN ONLY — never shown to a tenant) ---- */
    // What the tenant is quoted for the plan it is being given. The tenant side
    // has no access to any of this: the price list is a super-admin endpoint and
    // the summary renders only on this page.
    //
    // `discount.value` is kept as the raw STRING the user typed so the field can
    // be emptied mid-edit ('' is not 0). It is coerced once, at the boundary.
    const [discount, setDiscount] = useState({ type: null, value: '' });
    // THE PAYMENT TAB (doc/5.10.2026.md) — create mode only; an existing
    // tenant's tab reads and saves its own billing. `discount` above is
    // shared: the quote (planPricing) and plan 1 use the same one.
    const [billingForm, setBillingForm] = useState({ noFee: false, planEndAction: 'message', period: 'monthly' });

    const priceOf = useMemo(
        () => Object.fromEntries((featurePrices || []).map((p) => [p.featureId, p])),
        [featurePrices]
    );

    // Which features the tenant is actually being charged for. This must agree
    // with renderFeatureRow() exactly — a second interpretation of "on" would
    // quote a price for a plan the form is not saving. Derived from the same
    // three inputs it uses: the locked cores, whether the owning service is on,
    // and featureForm. Locked cores are NOT free: they are the service, so they
    // count whenever their service is on.
    const chargedFeatureIds = useMemo(() => {
        const out = new Set(); // a Set, so a feature listed under two groups can't be billed twice
        (featureGroups || []).forEach((g) => {
            const svcId = g.service;
            (g.features || []).forEach((f) => {
                const locked = isGlobalCore(f.id) || (svcId && svcId !== 'common' && isServiceCore(svcId, f.id));
                const gatedOff = svcId && svcId !== 'common' && !serviceOn(svcId);
                if (gatedOff || f.status === 'hold') return;
                if (locked ? serviceOn(svcId) : Boolean(featureForm[f.id])) out.add(f.id);
            });
        });
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [featureGroups, globalCoreIds, serviceCoreIds, services, featureForm]);

    const { subtotalMonthly, subtotalYearly } = useMemo(() => {
        let m = 0;
        let y = 0;
        chargedFeatureIds.forEach((id) => {
            m += Number(priceOf[id]?.monthlyPrice) || 0;
            y += Number(priceOf[id]?.yearlyPrice) || 0;
        });
        return { subtotalMonthly: m, subtotalYearly: y };
    }, [chargedFeatureIds, priceOf]);

    // One discountValue is subtracted from BOTH totals, so a flat rupee amount
    // is capped at the MONTHLY subtotal (the smaller of the two) — anything
    // larger would drive the monthly final negative. The finals are floored at
    // zero anyway; the server recomputes them regardless.
    const discountCap = discount.type === 'percentage' ? 100 : subtotalMonthly;
    const discountNumber = Math.min(Math.max(Number(discount.value) || 0, 0), discountCap);
    const applyDiscount = (base) => {
        if (!discount.type) return base;
        const off = discount.type === 'percentage' ? (base * discountNumber) / 100 : discountNumber;
        return Math.max(0, Math.round(base - off));
    };
    const finalMonthly = applyDiscount(subtotalMonthly);
    const finalYearly = applyDiscount(subtotalYearly);

    const changeDiscount = (next) => {
        setDiscount(next);
        markDirty();
    };
    const changeBilling = (patch) => {
        setBillingForm((b) => ({ ...b, ...patch }));
        markDirty();
    };

    // The charged features with both prices, for the Payment tab's list —
    // the same set the subtotal counts.
    const chargedLines = useMemo(() => {
        const labels = {};
        (featureGroups || []).forEach((g) => (g.features || []).forEach((f) => { labels[f.id] = f.label; }));
        return [...chargedFeatureIds].map((id) => ({
            featureId: id,
            label: labels[id] || id,
            monthly: Number(priceOf[id]?.monthlyPrice) || 0,
            yearly: Number(priceOf[id]?.yearlyPrice) || 0,
        }));
    }, [chargedFeatureIds, priceOf, featureGroups]);

    // …EXCEPT for the Mitra One Care open-platform tenant, which is the
    // platform's OWN tenant rather than a customer: there is nobody to quote, so
    // the whole price block is hidden and no planPricing is posted. The server
    // refuses to persist one for it too — not leaning on that is what stops this
    // page re-sending a figure that would then silently vanish on the next read.
    //
    // Gated on isEdit rather than read bare: `tenantMeta` holds whichever tenant
    // was loaded LAST and is never cleared when the create form opens, so a bare
    // read would blank the price block on a brand-new tenant merely because the
    // open-platform one happened to be the previous page.
    const isOpenPlatformTenant = isEdit && Boolean(tenantMeta?.isOpenPlatform);

    // Built at submit time, not memoised: `pricedAt` is "when this was quoted",
    // which is the moment of saving, not the moment of the last keystroke.
    const buildPlanPricing = () => ({
        subtotalMonthly,
        subtotalYearly,
        discountType: discount.type,
        discountValue: discount.type ? discountNumber : 0,
        finalMonthly,
        finalYearly,
        currency: 'INR',
        pricedAt: new Date().toISOString(),
    });

    /* ---------- per-hospital settings (login policy + starting values) ------ */
    const [settingsForm, setSettingsForm] = useState({});
    const settingsSource = exists && isEdit ? tenantSettings : settingsCatalogue;

    /* ---------- maintenance & storage (ported from the old modal) ---------- */
    const storage = useSelector((s) => s.tenant?.storage);
    const storageUsingPlatformDefault = useSelector((s) => s.tenant?.storageUsingPlatformDefault);
    const storageSaving = useSelector((s) => s.tenant?.storageSaving);
    const storageTesting = useSelector((s) => s.tenant?.storageTesting);
    const [storageForm, setStorageForm] = useState(EMPTY_STORAGE);
    const [storageErrors, setStorageErrors] = useState({});

    const maintenance = useSelector((st) => st.tenant?.maintenance);
    const maintenanceLoading = useSelector((st) => st.tenant?.maintenanceLoading);
    const maintenanceSaving = useSelector((st) => st.tenant?.maintenanceSaving);
    const [maintForm, setMaintForm] = useState({ name: '', surname: '', email: '', phone: '' });
    const [maintErrors, setMaintErrors] = useState({});
    const [tempPassword, setTempPassword] = useState(null);

    const setMaint = (key, value) => {
        let v = value;
        if (key === 'name') v = formatFirstName(v);
        if (key === 'surname') v = formatSurname(v);
        setMaintForm((prev) => ({ ...prev, [key]: v }));
        markDirty();
    };

    /* ---------------------------- loading ---------------------------- */
    useEffect(() => {
        if (!countryTimezoneList || countryTimezoneList.length === 0) {
            dispatch(docPatCreationCountryList());
        }
        dispatch(getFeatureCatalogue());
        dispatch(getFeaturePrices());
        if (isEdit) {
            if (!location.state?.tenant) dispatch(getTenantById(routeTenantId));
            dispatch(getTenantStorage(routeTenantId));
            dispatch(getTenantFeatures(routeTenantId));
            dispatch(getMaintenanceAdmin(routeTenantId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeTenantId]);

    // Seed the details form. Create starts clean; edit from the tenant row +
    // the meta getTenantFeatures returns (subdomain / connectionMode / type).
    useEffect(() => {
        if (!isEdit) { setForm(EMPTY); return; }
        if (!seedTenant) return;
        setForm((f) => ({
            ...f,
            name: seedTenant.name || '',
            countryId: seedTenant.countryId || '',
            contactEmail: seedTenant.contactEmail || '',
            contactPhone: seedTenant.contactPhone || '',
            city: seedTenant.city || '',
            supportLevel: seedTenant.supportLevel || '',
            patientIdPrefix: seedTenant.patientIdPrefix || '',
        }));
        setSavedPrefix(seedTenant.patientIdPrefix || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, seedTenant?.tenantId]);

    useEffect(() => {
        if (!isEdit || !tenantMeta) return;
        setForm((f) => ({
            ...f,
            subdomain: tenantMeta.subdomain || '',
            connectionMode: tenantMeta.connectionMode || 'connected',
            tenantType: tenantMeta.tenantType || f.tenantType,
            // Present only when the features response carries it (tenantSlice);
            // otherwise the tenant row's value seeded above stands.
            ...(tenantMeta.supportLevel !== undefined ? { supportLevel: tenantMeta.supportLevel || '' } : {}),
            ...(tenantMeta.hostMode !== undefined ? { hostMode: tenantMeta.hostMode || 'shared', appUrl: tenantMeta.appUrl || '' } : {}),
        }));
    }, [isEdit, tenantMeta]);

    // Only the DISCOUNT is hydrated — the subtotals are recomputed from the
    // ticked features, so a stale stored subtotal can never disagree with what
    // is on screen. Seeding, so no markDirty().
    //
    // Keyed on planPricing rather than the whole tenantMeta: the open-platform
    // toggle replaces tenantMeta without touching planPricing, and re-running
    // here would silently throw away a discount the user was mid-way through.
    const savedPlanPricing = tenantMeta?.planPricing;
    useEffect(() => {
        if (!isEdit) return;
        setDiscount(savedPlanPricing?.discountType
            ? { type: savedPlanPricing.discountType, value: String(savedPlanPricing.discountValue ?? '') }
            : { type: null, value: '' });
    }, [isEdit, savedPlanPricing]);

    // Feature defaults. Create: everything ON except defaultOff, before any
    // save exists. Edit: mirror the saved map.
    useEffect(() => {
        if (isEdit) {
            setFeatureForm(savedFeatures ? { ...savedFeatures } : {});
            return;
        }
        if (createdTenantId) return; // keep what was chosen at creation
        if (!Array.isArray(featureGroups) || featureGroups.length === 0) return;
        setFeatureForm((prev) => {
            if (Object.keys(prev).length) return prev;
            const next = {};
            featureGroups.forEach((g) => g.features.forEach((f) => { next[f.id] = !f.defaultOff && f.status !== 'hold'; }));
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, savedFeatures, featureGroups]);

    // Edit mode: the saved services map.
    useEffect(() => {
        if (!isEdit || !tenantServices) return;
        setServices({ ...tenantServices });
    }, [isEdit, tenantServices]);

    // Create mode: EVERY catalogue service held explicitly, false unless
    // ticked (30 Sep 2026). With an empty map the form posted only what was
    // ticked — { medi_consult: true } — and the server filled Pharma, Lab and
    // Radiology in from its defaults, so the tenant got four services while
    // the cards showed one. Now the cards and the request are the same map.
    useEffect(() => {
        if (isEdit || !Array.isArray(servicesCatalogue)) return;
        setServices((prev) => completeServices(prev));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, servicesCatalogue]);

    useEffect(() => {
        if (!Array.isArray(settingsSource)) return;
        const next = {};
        settingsSource.forEach((sItem) => {
            next[sItem.fekey] = sItem.type === 'value'
                ? (sItem.effectiveValue ?? '')
                : (sItem.effectiveStatus !== 'inactive');
        });
        setSettingsForm(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantSettings, settingsCatalogue, isEdit]);

    useEffect(() => {
        setTempPassword(null);
        setMaintErrors({});
        setMaintForm(maintenance
            ? {
                name: maintenance.name || '', surname: maintenance.surname || '',
                email: maintenance.email || '', phone: maintenance.phone || '',
            }
            : { name: '', surname: '', email: '', phone: '' });
    }, [maintenance]);

    useEffect(() => {
        if (!storage) { setStorageForm(EMPTY_STORAGE); return; }
        setStorageForm({
            bucket: storage.bucket || '', region: storage.region || 'ap-south-1',
            endpoint: storage.endpoint || '', keyPrefix: storage.keyPrefix || '',
            accessKeyId: '', secretAccessKey: '',
            isActive: storage.isActive !== false,
        });
    }, [storage]);

    /* -------------------- unsaved-changes guard -------------------- */
    // HashRouter is not a data router, so react-router's useBlocker cannot be
    // used here. The app already has a convention for this: a dirty page raises
    // `restrictnavigate`, the Sidebar intercepts its own nav clicks and answers
    // with `tryingnavigate` + `pendingNavigation`, and the dirty page shows the
    // dialog. Everything the Sidebar does NOT own — this page's own Back/Close,
    // the browser Back button, a refresh, a closed tab — is covered below, so
    // every way out of a half-filled tenant asks the same question.
    const askDiscard = () => Swal.fire({
        title: t('tenantpage.unsaved_title', 'Unsaved changes'),
        text: t('tenantpage.unsaved_text', 'This tenant has changes that were never saved. Leave and discard them?'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('tenantpage.unsaved_discard', 'Discard & leave'),
        confirmButtonColor: '#C0564A',
        cancelButtonText: t('tenantpage.unsaved_stay', 'Stay on this page'),
        cancelButtonColor: '#4ABDBD',
        allowOutsideClick: false,
        allowEscapeKey: false,
    }).then((r) => r.isConfirmed);

    useEffect(() => { dispatch(restrictnavigate(dirty)); }, [dirty, dispatch]);
    // Never leave the block latched on for whatever page comes next.
    useEffect(() => () => {
        dispatch(restrictnavigate(false));
        dispatch(clearPendingNavigation());
    }, [dispatch]);

    // The Sidebar (or any other blockingSlice caller) asked to leave.
    useEffect(() => {
        if (!trynavigate) return undefined;
        if (!dirty) { dispatch(tryingnavigate(false)); return undefined; }
        let alive = true;
        askDiscard().then((discard) => {
            if (!alive) return;
            if (discard) {
                // pendingNavigation is deliberately KEPT — the Sidebar performs
                // it as soon as the block clears, so the user lands where they
                // originally clicked instead of having to click twice.
                setDirty(false);
                dispatch(restrictnavigate(false));
                dispatch(tryingnavigate(false));
            } else {
                dispatch(tryingnavigate(false));
                dispatch(clearPendingNavigation());
            }
        });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trynavigate]);

    // Refresh / tab close — the browser's own dialog is the only option here.
    useEffect(() => {
        if (!dirty) return undefined;
        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [dirty]);

    // Browser Back. popstate fires AFTER the entry is already gone, so a
    // sacrificial entry is pushed while the form is dirty and re-pushed on every
    // catch — the page stays put until the question is answered.
    useEffect(() => {
        if (!dirty) return undefined;
        window.history.pushState(null, '', window.location.href);
        const onPop = () => {
            window.history.pushState(null, '', window.location.href);
            askDiscard().then((discard) => {
                if (!discard) return;
                setDirty(false);
                dispatch(restrictnavigate(false));
                navigate('/tenants');
            });
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirty]);

    // This page's own exits: Back, Cancel, Close.
    const guardedNavigate = async (to) => {
        if (dirty && !(await askDiscard())) return;
        setDirty(false);
        dispatch(restrictnavigate(false));
        dispatch(clearPendingNavigation());
        navigate(to);
    };

    /* ---------------------------- helpers ---------------------------- */
    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
        markDirty();
    };
    const setStorage = (k, v) => {
        setStorageForm((f) => ({ ...f, [k]: v }));
        setStorageErrors((e) => ({ ...e, [k]: undefined }));
        markDirty();
    };
    const setSetting = (fekey, v) => {
        setSettingsForm((prev) => ({ ...prev, [fekey]: v }));
        markDirty();
    };

    const settingsPayload = () => {
        const out = {};
        (settingsSource || []).forEach((sItem) => {
            const v = settingsForm[sItem.fekey];
            if (v === undefined) return;
            out[sItem.fekey] = sItem.type === 'value'
                ? { value: v === '' ? null : String(v) }
                : { status: v ? 'active' : 'inactive' };
        });
        return out;
    };

    /* ---------- tenant type — mandatory, over a master that can be empty ----- */
    // The chosen classification, for the header pill and the Details tab's
    // helper text. `label` is what the API sends; `name` is tolerated so the
    // pill doesn't blank out if the master ever ships the raw column name.
    //
    // The tenant_types master is NOT seeded and rows can be deleted after
    // tenants were already saved against them, so BOTH empty states below are
    // normal operation, not edge cases. Three situations have to be told apart:
    //   · catalogueReady — the catalogue response actually landed. `tenantTypes`
    //     starts as [] in the slice, which is indistinguishable from a genuinely
    //     empty master; `featureGroups` is static server config and is never
    //     empty, so it is the honest "the response arrived" signal and stops the
    //     empty-state hints flashing on every page load.
    //   · noTenantTypes — the master really is empty. A required dropdown with
    //     nothing in it is a dead end, so the field points at where to make one.
    //   · staleTenantType — this tenant stores a type id (e.g. the previously
    //     seeded 'hospital') whose row no longer exists. A blank picker would
    //     claim no type was ever chosen, so the raw id is shown and flagged.
    const selectedType = (tenantTypes || []).find((tt) => tt.id === form.tenantType);
    const typeLabel = selectedType?.label || selectedType?.name;
    const catalogueReady = Boolean((featureGroups || []).length);
    const noTenantTypes = catalogueReady && !(tenantTypes || []).length;
    const staleTenantType = catalogueReady && Boolean(form.tenantType) && !selectedType;

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = t('tenantpage.name_required', 'Tenant name is required');
        if (!isEdit && !form.countryId) e.countryId = t('tenantpage.country_required', 'Country is required and cannot be changed later');
        if (!form.contactEmail.trim()) e.contactEmail = t('tenantpage.email_required', 'Contact email is required');
        else if (!emailRe.test(form.contactEmail.trim())) e.contactEmail = t('tenantpage.email_invalid', 'Enter a valid email address');
        if (!form.contactPhone.trim()) e.contactPhone = t('tenantpage.phone_required', 'Contact phone is required');
        // Optional. Plain text only — the API refuses markup and anything over
        // CITY_MAX with the same rules, so this check is UX, not control.
        if (form.city.trim().length > CITY_MAX) e.city = t('tenantpage.city_too_long', 'City must be at most 100 characters');
        else if (/[<>]/.test(form.city)) e.city = t('tenantpage.city_invalid', 'City must be plain text — no < or >');
        // Mandatory since the type master became Super-Admin-managed — the API
        // rejects a create without one (400), so this check is UX, not control.
        // A stale id counts as empty: it names a type that no longer exists, and
        // saving it again would carry the dangling reference forward.
        if (!form.tenantType) e.tenantType = t('tenantpage.tenant_type_required', 'Tenant type is required');
        else if (staleTenantType) e.tenantType = t('tenantpage.tenant_type_stale_error', 'This type no longer exists — choose a current one');
        if (form.subdomain.trim() && !subdomainRe.test(form.subdomain.trim())) {
            e.subdomain = t('tenantpage.subdomain_invalid', '3–63 characters: lowercase letters, digits and inner hyphens only');
        }
        // Subdomain mode needs a subdomain to hang the address off, and the
        // address must be https (the API applies the same two rules).
        if (form.hostMode === 'subdomain') {
            const sub = (tenantMeta?.subdomain || form.subdomain).trim();
            if (!sub) e.hostMode = t('tenantpage.host_mode_needs_subdomain', 'Set a subdomain first');
            const url = form.appUrl.trim() || (sub ? `https://${sub}.${TENANT_DOMAIN}` : '');
            if (!/^https:\/\/[a-z0-9.-]+(\/[^\s?#]*)?$/i.test(url)) {
                e.appUrl = t('tenantpage.app_url_invalid', 'Enter the full https:// address, e.g. https://apollo.mitraonecare.com');
            }
        }
        // Empty = platform default; anything else must be a listed tier (the
        // API validates against the same SUPPORT_TIERS and would 400).
        if (form.supportLevel && !SUPPORT_SLA_OPTIONS.some((o) => o.value === form.supportLevel)) {
            e.supportLevel = t('tenantpage.support_sla_invalid', 'Choose one of the listed SLA levels');
        }
        // Optional; when given it must be the code shape the API accepts.
        if (form.patientIdPrefix.trim() && !prefixRe.test(form.patientIdPrefix.trim())) {
            e.patientIdPrefix = t('tenantpage.patient_code_invalid', 'Prefix must be 2–5 letters (e.g. APO)');
        }
        setErrors(e);
        if (Object.keys(e).length) setTab('details');
        return Object.keys(e).length === 0;
    };

    /* ---------------------------- submits ---------------------------- */

    // CREATE — one click makes the tenant, then applies services/features,
    // storage and maintenance to it. A failure after creation must never read
    // as "not created" (that is how a tenant gets made twice).
    const submitCreate = async () => {
        if (!validate()) return;
        // null, never a quote, for the platform's own tenant — the thunk and the
        // payload below both drop it. A tenant cannot be created as the open
        // platform (that toggle is edit-only), so this only ever matters for the
        // post-create saves, but the figure is built in one place either way.
        const planPricing = isOpenPlatformTenant ? null : buildPlanPricing();
        const payload = {
            name: form.name.trim(),
            countryId: form.countryId,
            contactEmail: form.contactEmail.trim(),
            contactPhone: form.contactPhone.trim(),
            city: form.city.trim() || null,
            connectionMode: form.connectionMode,
            supportLevel: form.supportLevel || null,
            services: completeServices(services),
            ...(planPricing ? { planPricing } : {}),
        };
        if (form.subdomain.trim()) payload.subdomain = form.subdomain.trim();
        if (form.tenantType) payload.tenantType = form.tenantType;
        payload.hostMode = form.hostMode;
        if (form.hostMode === 'subdomain' && form.appUrl.trim()) payload.appUrl = form.appUrl.trim();

        try {
            const created = await dispatch(createTenant(payload)).unwrap();
            const newId = created?.tenant?.tenantId || created?.tenantId;
            const failed = [];
            let shownPassword = null;
            let featuresSaved = false;

            if (newId) {
                try {
                    // planPricing is repeated here on purpose: creation is two
                    // calls, and the price belongs with whichever of them the
                    // server chose to persist it on. Both carry the identical
                    // object, so neither can win with a different figure.
                    await dispatch(saveTenantFeatures({
                        tenantId: newId, features: featureForm, settings: settingsPayload(), services: completeServices(services), planPricing,
                    })).unwrap();
                    featuresSaved = true;
                } catch { failed.push({ tab: 'services', label: t('tenantpage.tab_services', 'Services & Features') }); }
            }
            // PAYMENT TAB: No fee + plan-end choice, then plan 1 — AFTER the
            // features, because the plan prices what the server stored. If
            // the features failed, plan 1 waits (made later on this tab).
            if (newId) {
                try {
                    await saveTenantBillingSettings(newId, {
                        noFee: billingForm.noFee, planEndAction: billingForm.planEndAction,
                    });
                    if (!billingForm.noFee) {
                        if (!featuresSaved) throw new Error('features not saved');
                        const value = Number(discount.value) || 0;
                        await createTenantPlan(newId, {
                            mode: 'first',
                            period: billingForm.period,
                            discountType: discount.type,
                            discountValue: discount.type === 'percentage' ? Math.min(value, 100) : value,
                        });
                    }
                } catch { failed.push({ tab: 'payment', label: t('tenantBilling.tab_payment', 'Payment') }); }
            }
            if (newId && storageForm.bucket.trim()) {
                try {
                    const sPayload = {
                        provider: 's3',
                        bucket: storageForm.bucket.trim(),
                        region: storageForm.region.trim() || 'us-east-1',
                        endpoint: storageForm.endpoint.trim() || null,
                        keyPrefix: storageForm.keyPrefix.trim() || null,
                        isActive: storageForm.isActive,
                    };
                    if (storageForm.accessKeyId.trim()) sPayload.accessKeyId = storageForm.accessKeyId.trim();
                    if (storageForm.secretAccessKey.trim()) sPayload.secretAccessKey = storageForm.secretAccessKey.trim();
                    await dispatch(saveTenantStorage({ tenantId: newId, data: sPayload })).unwrap();
                } catch { failed.push({ tab: 'storage', label: t('tenantpage.tab_storage', 'Storage') }); }
            }
            if (newId && maintForm.email.trim()) {
                if (!emailRe.test(maintForm.email.trim())) {
                    setMaintErrors({ email: t('tenantpage.email_invalid', 'Enter a valid email address') });
                    failed.push({ tab: 'maintenance', label: t('tenantpage.tab_maintenance', 'Maintenance') });
                } else {
                    try {
                        const res = await dispatch(saveMaintenanceAdmin({
                            tenantId: newId,
                            data: {
                                name: maintForm.name.trim() || 'Maintenance',
                                surname: maintForm.surname.trim() || null,
                                email: maintForm.email.trim(),
                                phone: maintForm.phone.trim() || null,
                            },
                        })).unwrap();
                        if (res?.tempPassword) { setTempPassword(res.tempPassword); shownPassword = res.tempPassword; }
                    } catch { failed.push({ tab: 'maintenance', label: t('tenantpage.tab_maintenance', 'Maintenance') }); }
                }
            }

            if (newId && form.patientIdPrefix.trim()) {
                try {
                    await dispatch(setTenantPatientCodePrefix({
                        tenantId: newId, prefix: form.patientIdPrefix.trim().toUpperCase(),
                    })).unwrap();
                } catch { failed.push({ tab: 'details', label: t('tenantpage.patient_code_label', 'Patient ID prefix') }); }
            }

            setCreatedTenantId(newId);

            // Whatever DID save is no longer an unsaved change. A tab that
            // failed still is, so the guard keeps holding for it.
            setDirty(failed.length > 0);
            if (failed.length) {
                toast.warn(`${t('tenantpage.create_partial', 'Tenant created, but this could not be saved')}: ${failed.map((f) => f.label).join(', ')}`);
                setTab(failed[0].tab);
                return;
            }
            toast.success(t('tenantpage.create_success', 'Tenant created'));
            // The maintenance password is shown ONCE — leaving the page would
            // lose the only copy, so stay on it when there is one.
            if (shownPassword) { setTab('maintenance'); return; }
            navigate('/tenants');
        } catch (err) {
            toast.error(typeof err?.error === 'string' ? err.error : t('tenantpage.save_error', 'Failed to save tenant'));
        }
    };

    // EDIT — Details tab save.
    const submitDetails = async () => {
        if (!validate()) return;
        const payload = {
            name: form.name.trim(),
            contactEmail: form.contactEmail.trim(),
            contactPhone: form.contactPhone.trim(),
            // null clears the city.
            city: form.city.trim() || null,
            // null clears a hospital-level SLA back to the platform default.
            supportLevel: form.supportLevel || null,
        };
        if (form.tenantType) payload.tenantType = form.tenantType;
        // Subdomain is fixed once set; sending it when the tenant has none is
        // the one allowed first assignment.
        if (!tenantMeta?.subdomain && form.subdomain.trim()) payload.subdomain = form.subdomain.trim();
        // Sign-in host may change any time; the address only matters in
        // subdomain mode (empty = the API derives https://<subdomain>.<domain>).
        payload.hostMode = form.hostMode;
        if (form.hostMode === 'subdomain' && form.appUrl.trim()) payload.appUrl = form.appUrl.trim();
        try {
            await dispatch(updateTenant({ tenantId: workingTenantId, data: payload })).unwrap();

            // The prefix rides its own endpoint (backfill / rename side
            // effects live server-side). Sent only when actually changed;
            // an emptied field means "leave the stored prefix alone".
            const newPrefix = form.patientIdPrefix.trim().toUpperCase();
            if (newPrefix && newPrefix !== savedPrefix) {
                try {
                    const res = await dispatch(setTenantPatientCodePrefix({
                        tenantId: workingTenantId, prefix: newPrefix,
                    })).unwrap();
                    setSavedPrefix(newPrefix);
                    if (res?.renamed) {
                        toast.info(t('tenantpage.patient_code_renamed', '{{count}} patient codes renamed to the new prefix (numbers kept)', { count: res.renamed }));
                    } else if (res?.assigned) {
                        toast.info(t('tenantpage.patient_code_assigned', '{{count}} patients numbered', { count: res.assigned }));
                    }
                } catch (err) {
                    const msg = err?.error?.message || (typeof err?.error === 'string' ? err.error : null);
                    toast.error(msg || t('tenantpage.patient_code_error', 'Failed to set the patient ID prefix'));
                    return; // details saved, prefix not — keep the form dirty
                }
            }

            setDirty(false);
            toast.success(t('tenantpage.update_success', 'Tenant updated'));
        } catch (err) {
            toast.error(typeof err?.error === 'string' ? err.error : t('tenantpage.save_error', 'Failed to save tenant'));
        }
    };

    // EDIT / post-create — Services & Features + settings save.
    const submitFeatures = async () => {
        try {
            await dispatch(saveTenantFeatures({
                tenantId: workingTenantId,
                features: featureForm,
                settings: settingsPayload(),
                // Not sent until the saved map has loaded: the server reads a
                // service a posted map leaves out as OFF (30 Sep 2026), so an
                // empty map sent too early would switch every service off.
                services: Object.keys(services).length ? services : undefined,
                // Nothing to quote for the platform's own tenant; the thunk
                // omits the key entirely when this is falsy, so the tenant's
                // stored planPricing is left untouched rather than zeroed.
                planPricing: isOpenPlatformTenant ? null : buildPlanPricing(),
            })).unwrap();
            setDirty(false);
            toast.success(t('tenantpage.features_saved', 'Features updated'));
        } catch (err) {
            toast.error(typeof err?.error === 'string' ? err.error : t('tenantpage.features_save_error', 'Failed to update features'));
        }
    };

    const submitStorage = async () => {
        const e = {};
        if (!storageForm.bucket.trim()) e.bucket = t('tenantpage.bucket_required', 'Bucket name is required');
        if (!storage?.hasCredentials) {
            if (!storageForm.accessKeyId.trim()) e.accessKeyId = t('tenantpage.access_key_required', 'Access key is required');
            if (!storageForm.secretAccessKey.trim()) e.secretAccessKey = t('tenantpage.secret_key_required', 'Secret key is required');
        }
        setStorageErrors(e);
        if (Object.keys(e).length) return;
        const payload = {
            provider: 's3',
            bucket: storageForm.bucket.trim(),
            region: storageForm.region.trim() || 'us-east-1',
            endpoint: storageForm.endpoint.trim() || null,
            keyPrefix: storageForm.keyPrefix.trim() || null,
            isActive: storageForm.isActive,
        };
        if (storageForm.accessKeyId.trim()) payload.accessKeyId = storageForm.accessKeyId.trim();
        if (storageForm.secretAccessKey.trim()) payload.secretAccessKey = storageForm.secretAccessKey.trim();
        try {
            const res = await dispatch(saveTenantStorage({ tenantId: workingTenantId, data: payload })).unwrap();
            setDirty(false);
            if (res?.verified) toast.success(res.message || t('tenantpage.storage_saved', 'Storage configuration saved and verified'));
            else toast.warn(res?.message || t('tenantpage.storage_saved_unverified', 'Saved, but the connection test failed — left inactive.'));
        } catch (err) {
            toast.error(typeof err?.error === 'string' ? err.error : t('tenantpage.storage_save_error', 'Failed to save storage configuration'));
        }
    };

    const submitMaintenance = async () => {
        const e = {};
        if (!maintForm.email.trim()) e.email = t('tenantpage.maint_email_required', 'An email address is required');
        else if (!emailRe.test(maintForm.email.trim())) e.email = t('tenantpage.email_invalid', 'Enter a valid email address');
        setMaintErrors(e);
        if (Object.keys(e).length) return;
        try {
            const res = await dispatch(saveMaintenanceAdmin({
                tenantId: workingTenantId,
                data: {
                    name: maintForm.name.trim() || 'Maintenance',
                    surname: maintForm.surname.trim() || null,
                    email: maintForm.email.trim(),
                    phone: maintForm.phone.trim() || null,
                },
            })).unwrap();
            if (res?.tempPassword) setTempPassword(res.tempPassword);
            setDirty(false);
            toast.success(res?.created
                ? t('tenantpage.maint_created', 'Maintenance account created')
                : t('tenantpage.maint_updated', 'Maintenance account updated'));
        } catch (err) {
            toast.error(typeof err?.error === 'string' ? err.error : t('tenantpage.maint_save_error', 'Failed to save the maintenance account'));
        }
    };

    const toggleMaintenanceStatus = async () => {
        const next = maintenance?.status === 'active' ? 'inactive' : 'active';
        try {
            await dispatch(setMaintenanceAdminStatus({ tenantId: workingTenantId, status: next })).unwrap();
            toast.success(next === 'active'
                ? t('tenantpage.maint_enabled', 'Maintenance account enabled')
                : t('tenantpage.maint_disabled', 'Maintenance account disabled'));
        } catch (err) {
            toast.error(typeof err?.error === 'string' ? err.error : t('tenantpage.maint_status_error', 'Failed to update the account'));
        }
    };

    const runStorageTest = async () => {
        try {
            const res = await dispatch(testTenantStorage(workingTenantId)).unwrap();
            if (res?.ok) toast.success(t('tenantpage.storage_test_ok', 'Connection successful'));
            else toast.warn(`${t('tenantpage.storage_test_failed', 'Connection failed')}: ${res?.error || ''}`);
        } catch (err) {
            toast.error(typeof err?.error === 'string' ? err.error : t('tenantpage.storage_test_error', 'Could not run the connection test'));
        }
    };

    /* ---------------------------- rendering ---------------------------- */
    // selectedType / typeLabel / noTenantTypes / staleTenantType are derived up
    // beside validate(), which needs them too.

    const coreGroup = (featureGroups || []).find((g) => g.service === null);
    const serviceGroups = (featureGroups || []).filter((g) => g.service && g.service !== 'common');
    const commonGroup = (featureGroups || []).find((g) => g.service === 'common');
    const holdServices = (servicesCatalogue || []).filter((s) => s.status === 'hold');

    const loginMode = settingsForm['auth.loginIdentifier'];
    const bothMode = loginMode === 'both';
    // 'either' = ANY ONE contact, verified (owner, 17 Sep 2026). Like 'both' it
    // has no "second" identifier to configure — neither is mandatory on its own
    // — so the two sub-options are meaningless and are shown disabled.
    const eitherMode = loginMode === 'either';
    const subOptionsIgnored = bothMode || eitherMode;
    // The option labels come from the backend catalogue in English. They are
    // re-stated here so the console can translate them; an option the backend
    // adds later still renders, in English, through the catalogue fallback.
    const identifierOptionLabels = {
        email: t('tenantpage.identifier_opt_email', 'Email only'),
        mobile: t('tenantpage.identifier_opt_mobile', 'Mobile no only'),
        both: t('tenantpage.identifier_opt_both', 'Email or mobile'),
        either: t('tenantpage.identifier_opt_either', 'Email or mobile — any one, verified'),
    };
    // The two secondary-identifier toggles deliberately ignore the backend
    // catalogue's static label/description (owner request): the label is rebuilt
    // here to name the OTHER identifier, so it follows the primary-identifier
    // radio live, and the long description paragraph is dropped entirely.
    const otherIdentifierLabel = loginMode === 'mobile'
        ? t('tenantpage.identifier_email', 'email')
        : loginMode === 'email'
            ? t('tenantpage.identifier_mobile', 'mobile no')
            : t('tenantpage.identifier_both', 'email & mobile no');
    // Owner, 18 Sep 2026: the two sub-options are now written as the SENTENCES
    // they actually mean — "the e-mail is required too" / "the e-mail must be
    // verified when given" — rather than as the setting's internal name. Under
    // the platform default (mobile-first) they are the whole of the e-mail
    // rule, and "Second identifier OTP (email)" told a Super Admin nothing
    // about whether a blank box would be refused.
    const secondaryToggleLabels = {
        'auth.secondaryIdentifierRequired': t('tenantpage.second_identifier_required',
            'The {{identifier}} is required too', { identifier: otherIdentifierLabel }),
        'auth.secondaryIdentifierOtp': t('tenantpage.second_identifier_otp',
            'The {{identifier}} must be verified when given', { identifier: otherIdentifierLabel }),
    };
    const renderSettingCard = (sItem) => {
        // Only the two second-identifier switches are meaningless under
        // 'both' / 'either'. Other login-group switches (doctor
        // self-registration, 1 Oct 2026) keep working whatever the mode.
        const subDisabled = subOptionsIgnored && IDENTIFIER_SUB_KEYS.includes(sItem.fekey);
        return (
            <div key={sItem.fekey} data-testid={`tenant-setting-${sItem.fekey}`}
                className={`rounded-xl border border-gray-200 px-3 py-2.5 ${subDisabled ? 'opacity-50' : ''}`}>
                {sItem.type === 'value' ? (
                    <label className="flex flex-col gap-1.5">
                        <span className="text-xs font-semibold text-gray-700">{sItem.label}</span>
                        <span className="text-[11px] text-gray-500">{sItem.description}</span>
                        <select
                            value={settingsForm[sItem.fekey] ?? ''}
                            onChange={(e) => setSetting(sItem.fekey, e.target.value)}
                            className="tt-select mt-1 p-1.5 border border-gray-300 rounded-lg text-xs cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                        >
                            {(sItem.options || []).map((o) => (
                                <option key={o} value={o}>
                                    {sItem.optionLabels?.[o]
                                        || (o === 'none'
                                            ? t('tenantpage.no_restriction', 'No restriction')
                                            : t('tenantpage.n_months', '{{n}} months', { n: o }))}
                                </option>
                            ))}
                        </select>
                    </label>
                ) : (
                    <label className={`flex items-start gap-2.5 ${subDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input type="checkbox" checked={!!settingsForm[sItem.fekey]} disabled={subDisabled}
                            onChange={(e) => setSetting(sItem.fekey, e.target.checked)}
                            className="mt-0.5 accent-[#4ABDBD] cursor-pointer" />
                        <span className="min-w-0">
                            <span className="block text-xs font-semibold text-gray-700">
                                {secondaryToggleLabels[sItem.fekey] || sItem.label}
                            </span>
                            {!secondaryToggleLabels[sItem.fekey] && (
                                <span className="block text-[11px] text-gray-500">{sItem.description}</span>
                            )}
                        </span>
                    </label>
                )}
                {sItem.isOverridden && (
                    <span className="mt-1.5 inline-block text-[10px] font-semibold text-[#3AA8A8]">
                        {t('tenantpage.set_for_this_tenant', 'set for this hospital')}
                    </span>
                )}
            </div>
        );
    };

    const renderFeatureRow = (f, svcId) => {
        const locked = isGlobalCore(f.id) || (svcId && svcId !== 'common' && isServiceCore(svcId, f.id));
        const gatedOff = svcId && svcId !== 'common' && !serviceOn(svcId);
        // On hold: shown the way a held SERVICE is — off, disabled, tagged Soon.
        const onHold = f.status === 'hold';
        const checked = locked ? serviceOn(svcId) : Boolean(featureForm[f.id]);
        return (
            <label key={f.id}
                data-testid={onHold ? `tenant-feature-row-hold-${f.id}` : undefined}
                className={`flex items-start gap-2.5 px-2 py-2 rounded-lg ${
                    gatedOff || onHold ? 'opacity-45 cursor-not-allowed' : 'hover:bg-[#F4FAF9] cursor-pointer'
                }`}>
                <input
                    type="checkbox"
                    data-testid={`tenant-feature-${f.id}`}
                    checked={gatedOff || onHold ? false : checked}
                    disabled={locked || gatedOff || onHold}
                    onChange={(e) => toggleFeature(f.id, e.target.checked)}
                    className="mt-0.5 accent-[#4ABDBD]"
                />
                <span className="min-w-0">
                    <span className="text-[13px] font-semibold text-gray-700 flex items-center flex-wrap gap-2">
                        {f.label}
                        {locked && <Badge tone="core">{t('tenantpage.core', 'core')}</Badge>}
                        {onHold && <Badge tone="hold">{t('tenantpage.soon', 'Soon')}</Badge>}
                        {f.defaultOff && <Badge tone="gold">{t('tenantpage.open_platform', 'open platform')}</Badge>}
                    </span>
                    <span className="block text-[11px] text-gray-500 leading-snug">{f.description}</span>
                </span>
            </label>
        );
    };

    const medicineSourceItem = (settingsSource || []).find((sItem) => sItem.fekey === MEDICINE_SOURCE_KEY);
    const renderMedicineSource = () => {
        const pharmaOn = serviceOn('pharma');
        const raw = settingsForm[MEDICINE_SOURCE_KEY];
        const selected = MEDICINE_SOURCES.includes(raw) && (raw === 'manual' || pharmaOn) ? raw : 'manual';
        const labels = {
            manual: t('tenantpage.medicine_source_manual', 'Manual'),
            pharmacy: t('tenantpage.medicine_source_pharmacy', 'From pharmacy'),
            both: t('tenantpage.medicine_source_both', 'Both'),
        };
        const hints = {
            manual: t('tenantpage.medicine_source_manual_hint', 'Each doctor adds their own medicines and products.'),
            pharmacy: t('tenantpage.medicine_source_pharmacy_hint', "Doctors pick only from this organisation's pharmacy list."),
            both: t('tenantpage.medicine_source_both_hint', 'Doctors add their own and pick from the pharmacy.'),
        };
        return (
            <div data-testid="tenant-medicine-source" className="mx-3 mb-3 rounded-xl border border-gray-200 px-3 py-2.5">
                <div className="flex items-center gap-2">
                    <Pill className="w-4 h-4 text-[#4ABDBD]" />
                    <span className="text-xs font-semibold text-gray-700">
                        {t('tenantpage.medicine_source', 'Medicines source')}
                    </span>
                </div>
                <p className="text-[11px] text-gray-500 mt-0.5">
                    {t('tenantpage.medicine_source_hint', 'Where this organisation\'s doctors get their medicines and products from. The pharmacy itself does not change.')}
                </p>
                <div role="radiogroup" aria-label={t('tenantpage.medicine_source', 'Medicines source')}
                    className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                    {MEDICINE_SOURCES.map((o) => {
                        const disabled = o !== 'manual' && !pharmaOn;
                        const active = selected === o;
                        return (
                            <button key={o} type="button" role="radio" aria-checked={active}
                                data-testid={`tenant-medicine-source-${o}`}
                                disabled={disabled}
                                onClick={() => setSetting(MEDICINE_SOURCE_KEY, o)}
                                className={`text-left rounded-lg border px-3 py-2 transition ${
                                    active
                                        ? 'border-[#4ABDBD] bg-[#E7F5F4]'
                                        : 'border-gray-200 bg-white hover:border-[#4ABDBD]'
                                } ${disabled ? 'opacity-45 cursor-not-allowed hover:border-gray-200' : 'cursor-pointer'}`}>
                                <span className={`block text-xs font-semibold ${active ? 'text-[#2D8A8A]' : 'text-gray-700'}`}>
                                    {labels[o]}
                                </span>
                                <span className="block text-[11px] text-gray-500 leading-snug">{hints[o]}</span>
                            </button>
                        );
                    })}
                </div>
                {!pharmaOn && (
                    <p data-testid="tenant-medicine-source-needs-pharma" className="mt-1.5 text-[11px] text-amber-700">
                        {t('tenantpage.medicine_source_needs_pharma', 'From pharmacy and Both need the Pharma service.')}
                    </p>
                )}
            </div>
        );
    };

    const renderServiceCard = (g) => {
        const svc = serviceMap[g.service] || { label: g.label, role: null, status: 'available' };
        const on = serviceOn(g.service);
        return (
            <div key={g.id}
                data-testid={`tenant-service-${g.service}`}
                className={`rounded-2xl border ${on ? 'border-[#4ABDBD]/60' : 'border-gray-200'} bg-white overflow-hidden mb-3.5`}>
                <div className="flex items-center gap-3 px-4 py-3 bg-[#F7FBFA] flex-wrap">
                    <Switch checked={on} onChange={(v) => toggleService(g.service, v)}
                        testid={`tenant-service-toggle-${g.service}`} />
                    <h3 className="text-[15px] font-bold text-gray-800 flex-1 min-w-0">
                        {svc.label}
                        {svc.role && (
                            <span className="ml-2 text-xs font-normal text-gray-400">
                                {t('tenantpage.svc_role', 'role')}: {svc.role}
                            </span>
                        )}
                    </h3>
                    <span className="text-[11px] text-gray-400">
                        {g.features.length
                            ? t('tenantpage.n_features', '{{n}} features', { n: g.features.length })
                            : t('tenantpage.module_later_phase', 'module lands in a later phase')}
                    </span>
                </div>
                {g.features.length > 0 && (
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-x-5 px-3 py-2.5 ${on ? '' : 'pointer-events-none'}`}>
                        {g.features.map((f) => renderFeatureRow(f, g.service))}
                    </div>
                )}
                {g.service === 'medi_consult' && on && medicineSourceItem && renderMedicineSource()}
            </div>
        );
    };

    const tabs = [
        { id: 'details', label: t('tenantpage.tab_details', 'Details'), icon: Building2 },
        { id: 'services', label: t('tenantpage.tab_services', 'Services & Features'), icon: LayoutGrid },
        { id: 'policy', label: t('tenantpage.tab_policy', 'Login Policy'), icon: ShieldCheck },
        { id: 'maintenance', label: t('tenantpage.tab_maintenance', 'Maintenance'), icon: Wrench },
        { id: 'storage', label: t('tenantpage.tab_storage', 'Storage'), icon: HardDrive },
        { id: 'payment', label: t('tenantBilling.tab_payment', 'Payment'), icon: BadgeIndianRupee },
    ];

    const loginItems = (settingsSource || []).filter((sItem) => sItem.group === 'login');
    // The medicines source is drawn in the Medi-Consult card, not here (this
    // block is the hospital admin's own preferences).
    const generalItems = (settingsSource || []).filter((sItem) => sItem.group !== 'login' && sItem.group !== 'medicines');
    // The primary-identifier picker gets the mockup's 3-card radio row; every
    // other login-group setting (the two sub-option checkboxes) keeps the
    // generic renderer.
    const identifierItem = loginItems.find((i) => i.fekey === 'auth.loginIdentifier');
    const loginSubItems = loginItems.filter((i) => IDENTIFIER_SUB_KEYS.includes(i.fekey));
    // Every other login-group setting — today "Doctor self-registration"
    // (auth.doctorRegistration, per tenant since the owner's decision of
    // 1 Oct 2026; it used to be one platform-wide switch). Drawn under its own
    // heading, outside the "sub-options ignored" note, and saved through the
    // same settings payload as the identifier settings.
    const loginOtherItems = loginItems.filter(
        (i) => i.fekey !== 'auth.loginIdentifier' && !IDENTIFIER_SUB_KEYS.includes(i.fekey)
    );
    // WHICH MODE IS THE PLATFORM'S OWN DEFAULT — the `features` row every
    // tenant inherits when it has no override of its own. It is 'mobile' since
    // the owner's rule of 18 Sep 2026, and the card that carries it says so:
    // a Super Admin choosing a mode for a new hospital should be able to see
    // which one is the house answer, and an edit page should say out loud when
    // THIS hospital has been moved off it. `platformValue` comes straight from
    // loadTenantSettings; an older backend that omits it simply draws no badge.
    const platformDefaultMode = identifierItem?.platformValue || null;
    const selectedMode = identifierItem
        ? (settingsForm[identifierItem.fekey] ?? identifierItem.effectiveValue)
        : null;
    const modeOverridden = !!(platformDefaultMode && selectedMode && selectedMode !== platformDefaultMode);

    return (
        <div data-testid="tenant-form-page">
            {/* ---------------- header ---------------- */}
            <div className="flex items-center gap-3 flex-wrap mb-4">
                <button type="button" data-testid="tenant-form-back"
                    onClick={() => guardedNavigate('/tenants')}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#2D8A8A] cursor-pointer">
                    <ArrowLeft className="w-4 h-4" />
                    {t('tenantpage.back', 'Back')}
                </button>
                <h1 className="text-xl font-bold text-[#1a365d] flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#4ABDBD]" />
                    {isEdit ? t('tenantpage.edit_tenant', 'Edit Tenant') : t('tenantpage.add_tenant', 'Add Tenant')}
                </h1>
                {typeLabel && (
                    <span data-testid="tenant-type-pill"
                        className="text-[11px] font-bold text-[#2D8A8A] bg-[#E7F5F4] border border-[#4ABDBD] rounded-full px-3 py-1">
                        {typeLabel}
                    </span>
                )}
            </div>

            {/* ---------------- tabs ---------------- */}
            <div className="flex gap-1.5 flex-wrap border-b border-[#C7D6D5] mb-5">
                {tabs.map((tb, i) => (
                    <button key={tb.id} type="button"
                        data-testid={`tenant-form-tab-${tb.id}`}
                        onClick={() => setTab(tb.id)}
                        className={`flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-semibold rounded-t-xl transition-colors cursor-pointer ${
                            tab === tb.id ? 'bg-[#4ABDBD] text-white' : 'text-gray-500 hover:text-[#2D8A8A]'
                        }`}>
                        <tb.icon className="w-3.5 h-3.5" />
                        {`${i + 1} · ${tb.label}`}
                    </button>
                ))}
            </div>

            {/* ================= TAB: DETAILS ================= */}
            {tab === 'details' && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {t('tenantpage.name', 'Tenant name')} <span className="text-red-500">*</span>
                            </label>
                            <input data-testid="tenant-name-input" value={form.name}
                                onChange={(e) => set('name', e.target.value)}
                                placeholder={t('tenantpage.name_placeholder', 'e.g. Apollo Hospital')}
                                className={`${field} ${errors.name ? 'border-red-500' : 'border-gray-300'}`} />
                            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {t('tenantpage.country', 'Country')} {!isEdit && <span className="text-red-500">*</span>}
                            </label>
                            {isEdit ? (
                                <LockedValue>
                                    {countryTimezoneList?.find((c) => c.countryId === form.countryId)?.countryName || unlessSystemId(form.countryId, '—')}
                                </LockedValue>
                            ) : (
                                <>
                                    <SearchableSelect
                                        variant="form"
                                        value={form.countryId}
                                        onChange={(v) => set('countryId', v)}
                                        options={(countryTimezoneList || []).map((c) => ({ value: c.countryId, label: c.countryName }))}
                                        placeholder={t('tenantpage.select_country', 'Select a country')}
                                        searchPlaceholder={t('common.search', 'Search…')}
                                    />
                                    <p className="text-[11px] text-gray-400 mt-1">
                                        {t('tenantpage.country_create_hint', 'This cannot be changed later — doctors and admins inherit it.')}
                                    </p>
                                    {errors.countryId && <p className="text-red-500 text-xs mt-1">{errors.countryId}</p>}
                                </>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {t('tenantpage.city', 'City')}
                            </label>
                            <input data-testid="tenant-city-input" value={form.city}
                                onChange={(e) => set('city', e.target.value)}
                                maxLength={CITY_MAX}
                                placeholder={t('tenantpage.city_placeholder', 'e.g. Chennai')}
                                className={`${field} ${errors.city ? 'border-red-500' : 'border-gray-300'}`} />
                            <p className="text-[11px] text-gray-400 mt-1">
                                {t('tenantpage.city_hint', 'Shown at sign-in to tell hospitals of the same name apart.')}
                            </p>
                            {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                        </div>
                        {/* Tenant type — a CLASSIFICATION LABEL, nothing more. It
                            tells a patient "Small Clinic" from "Multispecialty
                            Hospital"; it decides no services and gates no feature,
                            which is why it lives here and not on the Services tab.
                            MANDATORY: the API rejects a create without one. */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {t('tenantpage.tenant_type', 'Tenant type')} <span className="text-red-500">*</span>
                            </label>
                            <SearchableSelect
                                testId="tenant-type-select"
                                variant="form"
                                value={form.tenantType}
                                onChange={(v) => set('tenantType', v)}
                                // A stale saved id is appended as its own DISABLED
                                // row, because SearchableSelect falls back to the
                                // placeholder for a value it has no option for — and
                                // an apparently-blank picker on a tenant that does
                                // have a type stored is the worst of the three
                                // states. Disabled: re-picking a type the master no
                                // longer has is not a choice worth offering, and the
                                // required check refuses it anyway.
                                options={[
                                    ...(tenantTypes || []).map((tt) => ({ value: tt.id, label: tt.label || tt.name })),
                                    ...(staleTenantType ? [{
                                        value: form.tenantType,
                                        // A readable preset slug ('hospital') is
                                        // shown; a generated id (TTY00001) is not
                                        // (no system id on screen, 29 Sep 2026).
                                        label: unlessSystemId(form.tenantType, '—'),
                                        disabled: true,
                                        note: t('tenantpage.tenant_type_gone', '(no longer available — pick a type)'),
                                    }] : []),
                                ]}
                                // "Select a…", not the old "No type selected":
                                // this row is now an instruction, not a state the
                                // tenant is allowed to be saved in.
                                placeholder={t('tenantpage.select_tenant_type', 'Select a tenant type')}
                                searchPlaceholder={t('common.search', 'Search…')}
                                // The control draws its own border, so the invalid
                                // state is a ring on the wrapper — same red, same
                                // place, without forking SearchableSelect.
                                className={errors.tenantType ? 'rounded-xl ring-1 ring-red-500' : ''}
                            />
                            {noTenantTypes && (
                                <p data-testid="tenant-type-empty-master"
                                    className="text-[11px] text-amber-700 mt-1 leading-snug">
                                    {t('tenantpage.tenant_type_none', 'No tenant types exist yet —')}{' '}
                                    <Link to="/tenant-type" className="font-semibold underline hover:text-[#2D8A8A]">
                                        {t('tenantpage.tenant_type_none_link', 'create one in Master → Tenant Type')}
                                    </Link>
                                </p>
                            )}
                            {staleTenantType && (
                                <p data-testid="tenant-type-stale"
                                    className="text-[11px] text-gray-500 mt-1 leading-snug">
                                    {t('tenantpage.tenant_type_stale_hint', 'Saved as “{{id}}”, which is no longer in the tenant-type master. Pick a current type — this one can no longer be saved.', { id: form.tenantType })}
                                </p>
                            )}
                            <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                                {selectedType?.description
                                    || t('tenantpage.tenant_type_hint', 'How this hospital is described — a label only. Which services it gets is chosen on the Services tab.')}
                            </p>
                            {errors.tenantType && <p className="text-red-500 text-xs mt-1">{errors.tenantType}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {t('tenantpage.contact_email', 'Contact email')} <span className="text-red-500">*</span>
                            </label>
                            <input data-testid="tenant-email-input" type="email" value={form.contactEmail}
                                onChange={(e) => set('contactEmail', e.target.value)}
                                placeholder="admin@hospital.com"
                                className={`${field} ${errors.contactEmail ? 'border-red-500' : 'border-gray-300'}`} />
                            {errors.contactEmail && <p className="text-red-500 text-xs mt-1">{errors.contactEmail}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {t('tenantpage.contact_phone', 'Contact phone')} <span className="text-red-500">*</span>
                            </label>
                            <input data-testid="tenant-phone-input" value={form.contactPhone}
                                onChange={(e) => set('contactPhone', e.target.value.replace(/[^\d+\-\s]/g, ''))}
                                maxLength={20} placeholder="+91 …"
                                className={`${field} ${errors.contactPhone ? 'border-red-500' : 'border-gray-300'}`} />
                            {errors.contactPhone && <p className="text-red-500 text-xs mt-1">{errors.contactPhone}</p>}
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {t('tenantpage.subdomain', 'Subdomain')}
                            </label>
                            {tenantMeta?.subdomain ? (
                                <LockedValue mono>{tenantMeta.subdomain}.{TENANT_DOMAIN}</LockedValue>
                            ) : (
                                <>
                                    <input data-testid="tenant-subdomain-input" value={form.subdomain}
                                        onChange={(e) => set('subdomain', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                        maxLength={63} placeholder="apollo"
                                        className={`${field} font-mono ${errors.subdomain ? 'border-red-500' : 'border-gray-300'}`} />
                                    <p className="text-[12px] font-mono text-[#2D8A8A] mt-1">
                                        {(form.subdomain.trim() || '____')}.{TENANT_DOMAIN}
                                    </p>
                                    <p className="text-[11px] text-gray-400 mt-0.5">
                                        {t('tenantpage.subdomain_hint', "nginx serves this tenant's login page on its own subdomain. Fixed once set.")}
                                    </p>
                                    {errors.subdomain && <p className="text-red-500 text-xs mt-1">{errors.subdomain}</p>}
                                </>
                            )}
                        </div>

                        {/* -------- sign-in host: temp (shared) or own subdomain -------- */}
                        <div className="md:col-span-2">
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {t('tenantpage.host_mode', 'Sign-in host')}
                            </label>
                            <div className="flex flex-col sm:flex-row gap-2.5">
                                <ChoiceCard
                                    testid="tenant-host-shared"
                                    icon={Users}
                                    selected={form.hostMode === 'shared'}
                                    onSelect={() => set('hostMode', 'shared')}
                                    title={t('tenantpage.host_shared_title', 'Temp mode — shared host')}
                                    desc={t('tenantpage.host_shared_desc', 'Users sign in at app.{{domain}} by typing this subdomain. The default until infra has created the DNS record.', { domain: TENANT_DOMAIN })}
                                />
                                <ChoiceCard
                                    testid="tenant-host-subdomain"
                                    icon={Link2}
                                    selected={form.hostMode === 'subdomain'}
                                    onSelect={() => set('hostMode', 'subdomain')}
                                    title={t('tenantpage.host_subdomain_title', 'Subdomain mode — own address')}
                                    desc={t('tenantpage.host_subdomain_desc', 'Users sign in only at the address below. The shared host refuses them and points here instead.')}
                                />
                            </div>
                            {errors.hostMode && <p className="text-red-500 text-xs mt-1">{errors.hostMode}</p>}
                            {form.hostMode === 'subdomain' && (
                                <div className="mt-3">
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                                        {t('tenantpage.app_url', 'Address (full URL)')}
                                    </label>
                                    <input data-testid="tenant-app-url-input" value={form.appUrl}
                                        onChange={(e) => set('appUrl', e.target.value.trim())}
                                        maxLength={255}
                                        placeholder={`https://${(tenantMeta?.subdomain || form.subdomain.trim()) || 'apollo'}.${TENANT_DOMAIN}`}
                                        className={`${field} font-mono ${errors.appUrl ? 'border-red-500' : 'border-gray-300'}`} />
                                    <p className="text-[11px] text-gray-400 mt-0.5">
                                        {t('tenantpage.app_url_hint', 'Leave empty to use https://<subdomain>.{{domain}}. Only switch once infra has created the DNS record.', { domain: TENANT_DOMAIN })}
                                    </p>
                                    {errors.appUrl && <p className="text-red-500 text-xs mt-1">{errors.appUrl}</p>}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* -------- connection mode (permanent) -------- */}
                    <SectionTitle>{t('tenantpage.connection_mode', 'Connection mode')}</SectionTitle>
                    {exists ? (
                        <LockedValue>
                            {form.connectionMode === 'isolated'
                                ? t('tenantpage.mode_isolated', 'Isolated — private to this tenant')
                                : t('tenantpage.mode_connected', 'MOC-connected — listed in the MOC patient app')}
                        </LockedValue>
                    ) : (
                        <>
                            <div className="flex flex-col sm:flex-row gap-2.5">
                                <ChoiceCard
                                    testid="tenant-mode-connected"
                                    icon={Globe2}
                                    selected={form.connectionMode === 'connected'}
                                    onSelect={() => set('connectionMode', 'connected')}
                                    title={t('tenantpage.mode_connected_title', 'MOC-connected')}
                                    desc={t('tenantpage.mode_connected_desc', 'Listed in the MOC patient app. Its patients become global MOC accounts. Doctors are reachable via the hospital path.')}
                                />
                                <ChoiceCard
                                    testid="tenant-mode-isolated"
                                    icon={ShieldOff}
                                    selected={form.connectionMode === 'isolated'}
                                    onSelect={() => set('connectionMode', 'isolated')}
                                    title={t('tenantpage.mode_isolated_title', 'Isolated')}
                                    desc={t('tenantpage.mode_isolated_desc', 'Hidden from the MOC patient app. Own patients only — the tenant app includes a patient login. Shares nothing with MOC or other tenants.')}
                                />
                            </div>
                            <div className="mt-3">
                                {form.connectionMode === 'isolated' ? (
                                    <Note tone="red" icon={AlertTriangle}>
                                        {t('tenantpage.iso_warning', 'Isolation is PERMANENT. It cannot be changed after creation — there is no isolated → connected migration.')}
                                    </Note>
                                ) : (
                                    <Note tone="gold" icon={AlertTriangle}>
                                        {t('tenantpage.conn_warning', "This choice is permanent. A connected tenant's patients are global MOC accounts from the first registration.")}
                                    </Note>
                                )}
                            </div>
                        </>
                    )}

                    {/* -------- support SLA (editable any time) -------- */}
                    {/* Hospital-level response SLA for tickets raised by this tenant's
                        admins, staff and patients. Doctors follow their plan's
                        support_level instead; "Platform default" defers to the
                        Settings → Support slider (feature support.defaultSla). */}
                    <SectionTitle>{t('tenantpage.support_sla', 'Support SLA')}</SectionTitle>
                    <div className="sm:max-w-sm">
                        <SearchableSelect
                            testId="tenant-support-sla-select"
                                variant="form"
                            icon={LifeBuoy}
                            value={form.supportLevel}
                            onChange={(v) => set('supportLevel', v)}
                            options={SUPPORT_SLA_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey, o.labelFallback) }))}
                            placeholder={t('tenantpage.support_sla_default', 'Platform default')}
                            searchPlaceholder={t('common.search', 'Search…')}
                            disabled={mutating}
                            className={errors.supportLevel ? 'rounded-xl ring-1 ring-red-500' : ''}
                        />
                        <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                            {t('tenantpage.support_sla_hint', "Response-time SLA for tickets raised by this hospital's admins, staff and patients. Doctors follow their plan's support level instead. Leave on Platform default to use the SLA set in Settings → Support.")}
                        </p>
                        {errors.supportLevel && <p className="text-red-500 text-xs mt-1">{errors.supportLevel}</p>}
                    </div>

                    {/* -------- patient ID prefix (super_admin override) -------- */}
                    {/* The hospital's own patient numbering (APO -> APO001…).
                        Normally the tenant admin sets it once in their Settings
                        and it locks; this field is the platform override — it
                        may CHANGE a locked prefix, and the server then renames
                        every existing code keeping its number. */}
                    <SectionTitle>{t('tenantpage.patient_code_label', 'Patient ID prefix')}</SectionTitle>
                    <div className="sm:max-w-sm">
                        <input
                            type="text"
                            data-testid="tenant-patient-code-input"
                            value={form.patientIdPrefix}
                            maxLength={5}
                            disabled={mutating}
                            onChange={(e) => set('patientIdPrefix', e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase())}
                            placeholder={t('tenantpage.patient_code_placeholder', 'e.g. APO')}
                            className={`${field} font-mono font-semibold tracking-widest ${errors.patientIdPrefix ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'}`}
                        />
                        <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                            {savedPrefix
                                ? t('tenantpage.patient_code_hint_locked', 'Changing the prefix renames every existing patient code — numbers are kept (APO034 → SUN034). Prefixes are unique across hospitals.')
                                : t('tenantpage.patient_code_hint', 'Usually the hospital admin sets this once in their Settings. Setting it here numbers all existing patients (oldest first). 2–5 letters, unique across hospitals.')}
                        </p>
                        {errors.patientIdPrefix && <p className="text-red-500 text-xs mt-1">{errors.patientIdPrefix}</p>}
                    </div>

                    {/* -------- MOC open-platform tenant (super_admin only, edit mode) -------- */}
                    {exists && isSuperAdminSession() && form.connectionMode === 'connected' && (
                        <>
                            <SectionTitle>{t('tenantpage.open_platform', 'Mitra One Care open platform')}</SectionTitle>
                            <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-gray-50">
                                <div className="flex items-start gap-3">
                                    <Sparkles className={`w-5 h-5 mt-0.5 ${tenantMeta?.isOpenPlatform ? 'text-[#D7A05C]' : 'text-gray-400'}`} />
                                    <div>
                                        <p className="text-sm font-semibold text-gray-800">
                                            {tenantMeta?.isOpenPlatform
                                                ? t('tenantpage.open_platform_on', 'This is the open-platform tenant')
                                                : t('tenantpage.open_platform_off', 'Not the open-platform tenant')}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-0.5 max-w-md">
                                            {t('tenantpage.open_platform_desc', "The one tenant every MOC patient reaches directly — its doctors are the open-platform directory, and its admin manages the global patient identity (moc_accounts). Exactly one tenant may have this on; turning it on here turns it off wherever it currently is.")}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    disabled={mutating}
                                    data-testid="tenant-open-platform-toggle"
                                    onClick={() => {
                                        const turningOn = !tenantMeta?.isOpenPlatform;
                                        Swal.fire({
                                            title: turningOn
                                                ? t('tenantpage.open_platform_confirm_on_title', 'Make this the open-platform tenant?')
                                                : t('tenantpage.open_platform_confirm_off_title', 'Remove open-platform status?'),
                                            text: turningOn
                                                ? t('tenantpage.open_platform_confirm_on_text', 'Any other tenant currently marked as the open platform will be un-marked.')
                                                : t('tenantpage.open_platform_confirm_off_text', 'Its open-platform doctor directory and MOC patient administration will stop working until another tenant is marked instead.'),
                                            icon: 'warning',
                                            showCancelButton: true,
                                            confirmButtonColor: '#4ABDBD',
                                        }).then((r) => {
                                            if (!r.isConfirmed) return;
                                            dispatch(setOpenPlatform({ tenantId: workingTenantId, isOpenPlatform: turningOn }))
                                                .unwrap()
                                                .then(() => toast.success(turningOn ? t('tenantpage.open_platform_set', 'Set as the open-platform tenant') : t('tenantpage.open_platform_unset', 'No longer the open-platform tenant')))
                                                .catch(() => toast.error(t('tenantpage.open_platform_error', 'Failed to update')));
                                        });
                                    }}
                                    className={`shrink-0 text-xs font-semibold px-3 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                                        tenantMeta?.isOpenPlatform ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-[#4ABDBD]/10 text-[#2D8A8A] hover:bg-[#4ABDBD]/20'
                                    }`}
                                >
                                    {tenantMeta?.isOpenPlatform ? t('tenantpage.open_platform_remove', 'Remove') : t('tenantpage.open_platform_make', 'Make open platform')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ================= TAB: SERVICES & FEATURES ================= */}
            {tab === 'services' && (
                <div>
                    {featuresLoading && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('tenantpage.loading', 'Loading...')}
                        </div>
                    )}

                    <div className="mt-3">
                        <Note tone="teal" icon={Info}>
                            <b>{t('tenantpage.doctor_sub_note_title', 'Doctor subscription note:')}</b>{' '}
                            {t('tenantpage.doctor_sub_note', "a tenant's Medi-Consult has no doctor subscription — the hospital buys the service and its doctors simply use it. Doctor subscription plans exist only on the MOC open platform (Tenant #1).")}
                        </Note>
                    </div>

                    {/* Global core */}
                    <SectionTitle first step={1}>{t('tenantpage.step_core', 'Global core — always on, locked')}</SectionTitle>
                    <div className="rounded-2xl border border-gray-200 bg-white px-3 py-2 grid grid-cols-1 md:grid-cols-2 gap-x-5">
                        {(coreGroup?.features || []).map((f) => (
                            <label key={f.id} className="flex items-start gap-2.5 px-2 py-2">
                                <input type="checkbox" checked disabled className="mt-0.5 accent-[#4ABDBD]" />
                                <span className="min-w-0">
                                    <span className="text-[13px] font-semibold text-gray-700 flex items-center gap-2">
                                        {f.label} <Badge tone="core">{t('tenantpage.core', 'core')}</Badge>
                                    </span>
                                    <span className="block text-[11px] text-gray-500 leading-snug">{f.description}</span>
                                </span>
                            </label>
                        ))}
                    </div>
                    <div className="mt-3">
                        <Note tone="teal" icon={CalendarClock}>
                            <b>{t('tenantpage.booking_engine_title', 'One booking engine behind everything')}</b> —{' '}
                            {t('tenantpage.booking_engine_note', 'book / reschedule / cancel / completed. Each service switched ON plugs its own booking type into it: consult appointment · lab sample slot · scan slot · care visit. Queue & token runs inside this engine — walk-in tokens supported, configurable per doctor (Settings → Calendar) and per counter (tenant admin settings).')}
                        </Note>
                    </div>

                    {/* Services — ALL of them, always. Nothing hides a service or
                        marks one "optional": what a tenant buys is exactly what is
                        ticked here. */}
                    <SectionTitle step={2}>{t('tenantpage.step_services', "Services & their features — each locks its own core")}</SectionTitle>
                    {serviceGroups.map(renderServiceCard)}
                    {/* A service on HOLD is shown, tagged and left off — the server
                        force-offs it whatever is posted, so offering the switch
                        would be a lie. It still appears in tier 5's chips. */}
                    {holdServices.map((s) => (
                        <div key={s.id} className="rounded-2xl border border-gray-200 bg-white opacity-60 mb-3.5"
                            data-testid={`tenant-service-${s.id}`}>
                            <div className="flex items-center gap-3 px-4 py-3 bg-[#F7FBFA] flex-wrap">
                                <Switch checked={false} disabled onChange={() => {}}
                                    testid={`tenant-service-toggle-${s.id}`} />
                                <h3 className="text-[15px] font-bold text-gray-500 flex-1 min-w-0">{s.label}</h3>
                                <Badge tone="hold">{t('tenantpage.soon', 'Soon')}</Badge>
                                <span className="text-[11px] text-gray-400">{s.description}</span>
                            </div>
                        </div>
                    ))}

                    {/* Common */}
                    <SectionTitle step={3}>{t('tenantpage.step_common', 'Common — every tenant, works across all ON services')}</SectionTitle>
                    <div className="rounded-2xl border border-gray-200 bg-white px-3 py-2 grid grid-cols-1 md:grid-cols-2 gap-x-5">
                        {(commonGroup?.features || []).map((f) => renderFeatureRow(f, 'common'))}
                    </div>

                    <div className="mt-4">
                        <Note tone="gold" icon={SlidersHorizontal}>
                            {t('tenantpage.dependency_hint', 'Dependencies are enforced: a feature switched ON switches on what it needs; switched OFF switches off everything that needs it. Core features can never be unticked. Turning a feature off only hides data — nothing is deleted.')}
                        </Note>
                    </div>
                </div>
            )}

            {/* ================= TAB: LOGIN POLICY & SETTINGS ================= */}
            {tab === 'policy' && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                    {loginItems.length > 0 && (
                        <div className="mb-6">
                            <div className="flex items-baseline justify-between gap-3 mb-2">
                                <h4 className="text-sm font-bold text-[#1a365d]">
                                    {t('tenantpage.login_policy', 'Login & registration policy')}
                                </h4>
                                <span className="text-[10px] font-semibold text-[#D7A05C] uppercase tracking-wide">
                                    {t('tenantpage.super_admin_only', 'super admin only')}
                                </span>
                            </div>
                            <p className="text-[11px] text-gray-500 mb-3 leading-relaxed">
                                {t('tenantpage.login_policy_hint', "How this hospital's users sign in and register. The primary identifier is always mandatory with OTP verification; the two sub-options govern the other identifier. Under “Email or mobile”, both are mandatory with OTP; under “Email or mobile — any one, verified”, neither is mandatory on its own and one verified contact is enough. Only you can change this — the hospital's admin cannot.")}
                            </p>

                            {identifierItem && (
                                <div className="flex flex-col sm:flex-row gap-2.5 mb-3">
                                    {(identifierItem.options || []).map((o) => (
                                        <ChoiceCard
                                            key={o}
                                            testid={`tenant-login-identifier-${o}`}
                                            selected={selectedMode === o}
                                            onSelect={() => setSetting(identifierItem.fekey, o)}
                                            title={identifierOptionLabels[o] || identifierItem.optionLabels?.[o] || o}
                                            desc={o === platformDefaultMode
                                                ? t('tenantpage.platform_default', 'Platform default')
                                                : undefined}
                                        />
                                    ))}
                                </div>
                            )}
                            {modeOverridden && (
                                <p data-testid="tenant-login-identifier-override"
                                    className="text-[11px] text-[#D7A05C] font-semibold mb-3 -mt-1">
                                    {t('tenantpage.identifier_overridden',
                                        'This hospital is set away from the platform default — its own choice wins.')}
                                </p>
                            )}
                            {subOptionsIgnored && (
                                <div className="mb-3">
                                    <Note tone="teal" icon={Info}>
                                        {eitherMode
                                            ? t('tenantpage.login_policy_either_hint', 'One contact is enough: neither the e-mail nor the mobile number is mandatory on its own, but one of the contacts given must be verified with a one-time code — the sub-options below are ignored.')
                                            : t('tenantpage.login_policy_both_hint', 'Both identifiers are always required with OTP in this mode — the sub-options below are ignored.')}
                                    </Note>
                                </div>
                            )}
                            {loginSubItems.length > 0 && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {loginSubItems.map(renderSettingCard)}
                                </div>
                            )}
                            {loginOtherItems.length > 0 && (
                                <div className="mt-5" data-testid="tenant-login-registration">
                                    <h5 className="text-xs font-bold text-[#1a365d] mb-2">
                                        {t('tenantpage.doctor_registration_heading', 'Doctor self-registration')}
                                    </h5>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {loginOtherItems.map(renderSettingCard)}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {generalItems.length > 0 && (
                        <>
                            <h4 className="text-sm font-bold text-[#1a365d] mb-2">
                                {t('tenantpage.starting_settings', 'Starting settings for this hospital')}
                            </h4>
                            <p className="text-[11px] text-gray-500 mb-3 leading-relaxed">
                                {t('tenantpage.starting_settings_hint', "This hospital's own admin manages these from their Settings page — you are only choosing where they start. Each hospital has its own value, so a change here affects this tenant only.")}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {generalItems.map(renderSettingCard)}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ================= TAB: MAINTENANCE ================= */}
            {tab === 'maintenance' && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-col gap-4">
                    {maintenanceLoading ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('tenantpage.loading', 'Loading...')}
                        </div>
                    ) : (
                        <>
                            <div className="rounded-xl px-3 py-2.5 text-xs flex items-start gap-2 bg-gray-50 text-gray-600">
                                <Wrench className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <span>
                                    {t('tenantpage.maint_hint', 'An admin login inside this tenant for the platform maintenance team. Optional. It is a real account with its own audit trail, and is not counted as the tenant’s own staff.')}
                                </span>
                            </div>
                            {!exists && (
                                <div className="rounded-xl px-3 py-2 text-[11px] bg-[#E7F5F4] text-[#2D8A8A]">
                                    {t('tenantpage.maint_on_create', 'Fill in an email here and the account is created together with the tenant.')}
                                </div>
                            )}
                            {maintenance && exists ? (
                                <div className="flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2.5">
                                    <div className="min-w-0">
                                        <p className="text-xs font-semibold text-gray-700">
                                            {[maintenance.name, maintenance.surname].filter(Boolean).join(' ')
                                                || maintenance.email
                                                || t('support.sender_admin', 'Admin')}
                                        </p>
                                        <p className="text-[11px] text-gray-500">
                                            {t('tenantpage.maint_logins', 'Sign-ins')}: {maintenance.loginCount ?? 0}
                                            {maintenance.lastSeen && ` · ${new Date(maintenance.lastSeen).toLocaleDateString()}`}
                                        </p>
                                    </div>
                                    <button type="button" data-testid="maint-status-toggle"
                                        onClick={toggleMaintenanceStatus}
                                        className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold cursor-pointer ${
                                            maintenance.status === 'active'
                                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                        }`}>
                                        {maintenance.status === 'active'
                                            ? t('tenantpage.maint_active_disable', 'Active — click to disable')
                                            : t('tenantpage.maint_inactive_enable', 'Disabled — click to enable')}
                                    </button>
                                </div>
                            ) : exists && (
                                <div className="rounded-xl px-3 py-2 text-[11px] bg-amber-50 text-amber-800">
                                    {t('tenantpage.maint_none', 'No maintenance account for this tenant yet. Fill in an email and save to create one.')}
                                </div>
                            )}
                            {tempPassword && (
                                <div className="rounded-xl px-3 py-2.5 bg-[#4ABDBD]/10 border border-[#4ABDBD]/40">
                                    <p className="text-[11px] font-semibold text-[#2D8A8A] flex items-center gap-1.5">
                                        <KeyRound className="w-3.5 h-3.5" />
                                        {t('tenantpage.maint_temp_pw', 'Temporary password (shown once)')}
                                    </p>
                                    <p className="mt-1 font-mono text-sm text-gray-800 select-all">{tempPassword}</p>
                                    <p className="mt-1 text-[11px] text-gray-500">
                                        {t('tenantpage.maint_temp_pw_hint', 'Copy it now — it cannot be retrieved later. The account should change it after first sign-in.')}
                                    </p>
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">{t('tenantpage.maint_name', 'Name')}</label>
                                    <input data-testid="maint-name-input" value={maintForm.name}
                                        onChange={(e) => setMaint('name', e.target.value)}
                                        placeholder="Maintenance" className={`${field} border-gray-300`} />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">{t('tenantpage.maint_surname', 'Surname')}</label>
                                    <input data-testid="maint-surname-input" value={maintForm.surname}
                                        onChange={(e) => setMaint('surname', e.target.value)}
                                        placeholder="Team" className={`${field} border-gray-300`} />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                                        {t('tenantpage.maint_email', 'Login email')} <span className="text-red-500">*</span>
                                    </label>
                                    <input data-testid="maint-email-input" type="email" value={maintForm.email}
                                        onChange={(e) => { setMaintForm((f) => ({ ...f, email: e.target.value })); setMaintErrors({}); }}
                                        placeholder="maintenance@yourcompany.com"
                                        className={`${field} ${maintErrors.email ? 'border-red-500' : 'border-gray-300'}`} />
                                    {maintErrors.email && <p className="text-red-500 text-xs mt-1">{maintErrors.email}</p>}
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">{t('tenantpage.maint_phone', 'Phone')}</label>
                                    <input data-testid="maint-phone-input" value={maintForm.phone}
                                        onChange={(e) => setMaintForm((f) => ({ ...f, phone: e.target.value.replace(/[^\d+\-\s]/g, '') }))}
                                        maxLength={20} className={`${field} border-gray-300`} />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ================= TAB: STORAGE ================= */}
            {tab === 'storage' && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-col gap-4">
                    <div className={`rounded-xl px-3 py-2.5 text-xs flex items-start gap-2 ${
                        exists && !storageUsingPlatformDefault ? 'bg-[#E7F5F4] text-[#2D8A8A]' : 'bg-[#FBF1E4] text-[#7A5320]'
                    }`}>
                        <HardDrive className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span data-testid="storage-status-banner">
                            {exists && !storageUsingPlatformDefault
                                ? t('tenantpage.storage_using_own', "This tenant's files are stored in its own bucket.")
                                : t('tenantpage.storage_using_platform', "This tenant's files are stored in the platform's shared bucket. Configure a bucket below to keep them separate.")}
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('tenantpage.provider', 'Provider')}</label>
                            <div className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-600">
                                {t('tenantpage.provider_s3', 'S3-compatible (AWS / MinIO / R2 / Wasabi)')}
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {t('tenantpage.bucket', 'Bucket name')} <span className="text-red-500">*</span>
                            </label>
                            <input data-testid="storage-bucket-input" value={storageForm.bucket}
                                onChange={(e) => setStorage('bucket', e.target.value)}
                                placeholder="tenant-apollo-files"
                                className={`${field} ${storageErrors.bucket ? 'border-red-500' : 'border-gray-300'}`} />
                            {storageErrors.bucket && <p className="text-red-500 text-xs mt-1">{storageErrors.bucket}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('tenantpage.region', 'Region')}</label>
                            <input data-testid="storage-region-input" value={storageForm.region}
                                onChange={(e) => setStorage('region', e.target.value)}
                                placeholder="ap-south-1" className={`${field} border-gray-300`} />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('tenantpage.endpoint', 'Endpoint')}</label>
                            <input data-testid="storage-endpoint-input" value={storageForm.endpoint}
                                onChange={(e) => setStorage('endpoint', e.target.value)}
                                placeholder={t('tenantpage.endpoint_placeholder', 'empty = AWS S3')}
                                className={`${field} border-gray-300`} />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">{t('tenantpage.key_prefix', 'Key prefix')}</label>
                            <input data-testid="storage-prefix-input" value={storageForm.keyPrefix}
                                onChange={(e) => setStorage('keyPrefix', e.target.value)}
                                placeholder={t('tenantpage.key_prefix_placeholder', 'optional folder prefix')}
                                className={`${field} border-gray-300`} />
                        </div>
                        <div className="flex items-end pb-1">
                            <label className="flex items-center gap-2.5 cursor-pointer">
                                <input type="checkbox" checked={storageForm.isActive}
                                    onChange={(e) => setStorage('isActive', e.target.checked)}
                                    className="accent-[#4ABDBD]" />
                                <span className="text-xs font-semibold text-gray-600">{t('tenantpage.storage_active', 'Active')}</span>
                            </label>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {t('tenantpage.access_key', 'Access key ID')}
                                {!storage?.hasCredentials && <span className="text-red-500"> *</span>}
                            </label>
                            <input data-testid="storage-access-key-input" value={storageForm.accessKeyId}
                                onChange={(e) => setStorage('accessKeyId', e.target.value)}
                                placeholder={storage?.hasCredentials ? t('tenantpage.keep_stored', 'blank = keep the stored key') : 'AKIA…'}
                                className={`${field} ${storageErrors.accessKeyId ? 'border-red-500' : 'border-gray-300'}`} />
                            {storageErrors.accessKeyId && <p className="text-red-500 text-xs mt-1">{storageErrors.accessKeyId}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {t('tenantpage.secret_key', 'Secret access key')}
                                {!storage?.hasCredentials && <span className="text-red-500"> *</span>}
                            </label>
                            <input data-testid="storage-secret-key-input" type="password" value={storageForm.secretAccessKey}
                                onChange={(e) => setStorage('secretAccessKey', e.target.value)}
                                placeholder={storage?.hasCredentials ? t('tenantpage.keep_stored', 'blank = keep the stored key') : '••••••••'}
                                className={`${field} ${storageErrors.secretAccessKey ? 'border-red-500' : 'border-gray-300'}`} />
                            {storageErrors.secretAccessKey && <p className="text-red-500 text-xs mt-1">{storageErrors.secretAccessKey}</p>}
                        </div>
                    </div>
                </div>
            )}

            {/* ================= TAB: PAYMENT (doc/5.10.2026.md) ================= */}
            {tab === 'payment' && (exists ? (
                <TenantPaymentTab mode="edit" tenantId={workingTenantId}
                    onOpenPlan={() => guardedNavigate(`/tenants/${workingTenantId}/plan`)} />
            ) : (
                <TenantPaymentTab mode="create" billing={billingForm} onBillingChange={changeBilling}
                    lines={chargedLines} discount={discount} onDiscountChange={changeDiscount}
                    pricesMissing={!featurePricesLoading && !(featurePrices || []).length} />
            ))}

            {/* ================= sticky action bar ================= */}
            <div className="sticky bottom-0 mt-6 py-3.5 bg-white/95 backdrop-blur border-t border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                {/* Plan value. Shown on the Services tab only — that is the tab
                    whose Save routes to submitFeatures, so this is the one place
                    the figure and the plan it prices are saved together.
                    The open-platform tenant gets a sentence instead of a total:
                    an empty gap where a price used to be reads as a bug, and a
                    ₹0 total would read as a customer who pays nothing. */}
                {tab === 'services' && isOpenPlatformTenant && (
                    <p data-testid="tenant-plan-pricing-open-platform"
                        className="min-w-0 flex items-center gap-1.5 text-[11px] text-gray-500">
                        <Sparkles className="w-3.5 h-3.5 flex-none text-[#D7A05C]" />
                        {t('tenantpage.open_platform_no_price', 'Mitra One Care platform tenant — no plan value')}
                    </p>
                )}
                {tab === 'services' && !isOpenPlatformTenant && (
                    <div data-testid="tenant-plan-pricing" className="min-w-0">
                        <p className="flex items-center gap-1.5 text-[9px] font-extrabold uppercase tracking-widest text-[#D7A05C]">
                            <Tag className="w-3 h-3 flex-none" />
                            {t('tenantpage.pricing_internal', 'Super Admin only — not shown to the tenant')}
                        </p>
                        {/* The price list failing to load leaves every subtotal at
                            zero, and a silent ₹0 would be recorded as a real quote.
                            Say so rather than letting the figure speak for itself. */}
                        {!featurePricesLoading && !(featurePrices || []).length && (
                            <p className="flex items-center gap-1.5 text-[11px] text-amber-700 mt-0.5">
                                <AlertTriangle className="w-3.5 h-3.5 flex-none" />
                                {t('tenantpage.no_feature_prices', 'No feature prices are available — this plan would be recorded at zero.')}
                            </p>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2">
                            <span className="text-[11px] text-gray-500" data-testid="tenant-plan-subtotal">
                                {t('tenantpage.subtotal', 'Subtotal')}{' '}
                                <b className="text-gray-700">{inr(subtotalMonthly)}</b>
                                <span className="text-gray-400">/{t('tenantpage.per_month_short', 'mo')}</span>
                                <span className="mx-1 text-gray-300">·</span>
                                <b className="text-gray-700">{inr(subtotalYearly)}</b>
                                <span className="text-gray-400">/{t('tenantpage.per_year_short', 'yr')}</span>
                            </span>

                            {/* Discount, period and total moved to the Payment tab
                                (doc/5.10.2026.md): that is where the plan is made. */}
                            <button type="button" data-testid="tenant-plan-go-payment" onClick={() => setTab('payment')}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-300 text-[11px] font-semibold text-gray-600 hover:border-[#4ABDBD] hover:text-[#2D8A8A] cursor-pointer">
                                <BadgeIndianRupee className="w-3.5 h-3.5" />
                                {t('tenantBilling.go_payment', 'Discount and plan: Payment tab')}
                            </button>
                        </div>
                    </div>
                )}

                {/* ml-auto, not justify-between alone: on every tab except
                    Services the price block is absent and the buttons would
                    otherwise slide to the left edge. */}
                <div className="flex flex-wrap justify-end gap-2 sm:ml-auto">
                    <button type="button" onClick={() => guardedNavigate('/tenants')} disabled={mutating}
                        className="px-4 py-2 text-sm rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer disabled:opacity-50">
                        {exists ? t('tenantpage.close', 'Close') : t('tenantpage.cancel', 'Cancel')}
                    </button>
                    {exists && tab === 'storage' && storage?.hasCredentials && (
                        <button type="button" data-testid="storage-test-button" onClick={runStorageTest}
                            disabled={storageTesting || storageSaving}
                            className="px-4 py-2 text-sm rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold hover:bg-[#4ABDBD]/5 transition-all cursor-pointer disabled:opacity-50 flex items-center gap-2">
                            {storageTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <HardDrive className="w-4 h-4" />}
                            {t('tenantpage.test_connection', 'Test connection')}
                        </button>
                    )}
                    {!exists && (
                        <button type="button" data-testid="tenant-submit-button" onClick={submitCreate}
                            disabled={mutating || featuresSaving}
                            className="px-5 py-2 text-sm rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white font-semibold shadow-lg hover:shadow-xl transition-all cursor-pointer disabled:opacity-50 flex items-center gap-2">
                            {mutating && <Loader2 className="w-4 h-4 animate-spin" />}
                            {t('tenantpage.create_tenant', 'Create Tenant')}
                        </button>
                    )}
                    {exists && (tab === 'details' || tab === 'services' || tab === 'policy') && (
                        <button type="button" data-testid="tenant-submit-button"
                            onClick={tab === 'details' ? submitDetails : submitFeatures}
                            disabled={mutating || featuresSaving}
                            className="px-5 py-2 text-sm rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white font-semibold shadow-lg hover:shadow-xl transition-all cursor-pointer disabled:opacity-50 flex items-center gap-2">
                            {(mutating || featuresSaving) && <Loader2 className="w-4 h-4 animate-spin" />}
                            {t('tenantpage.save', 'Save')}
                        </button>
                    )}
                    {exists && (tab === 'maintenance' || tab === 'storage') && (
                        <button type="button" data-testid="tenant-submit-button"
                            onClick={tab === 'storage' ? submitStorage : submitMaintenance}
                            disabled={storageSaving || maintenanceSaving}
                            className="px-5 py-2 text-sm rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white font-semibold shadow-lg hover:shadow-xl transition-all cursor-pointer disabled:opacity-50 flex items-center gap-2">
                            {(storageSaving || maintenanceSaving) && <Loader2 className="w-4 h-4 animate-spin" />}
                            {t('tenantpage.save', 'Save')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
